import React, { useEffect, useRef, useState, useCallback } from 'react';
import { StreamPixelApplication, Flags, EditTextModal } from 'streampixel';
import axios from 'axios';
import { isTablet, isDesktop, isIOS, osName, browserName } from 'react-device-detect';
import { uuidv7 } from "uuidv7";

import LoadingUI from './LoadingUi';
import './loading.css';
import VoiceChatUI from './components/VoiceChatUI';

import { resolutionDef, QP_BY_CODEC, HOST_WAIT_MS, SFU_LOADING_TEXT } from './constants/streamConstants';
import { isMobilePhone, getDeviceTypeByUserAgent } from './utils/deviceUtils';
import { generateRandomName, createUserAvatar } from './utils/avatarUtils';
import { showLoadingSpinner, hideLoadingSpinner } from './utils/spinnerUtils';
import { captureScreenshot } from './utils/screenshotUtils';
import { getParentFrameUrl, getAttributionUrl } from './utils/urlUtils';
import {
  getWebRtcDiagnostics,
  logWebRtcDiagnostics,
  isTcpRelayActive,
  getSelectedPathInfo,
  findPcForTcpCheck,
} from './utils/webRtcDiagnostics';
import { createMediaHealthMonitor, CLASS as MEDIA_CLASS } from './utils/mediaHealth';

import StatsModal from './components/StatsModal';
import TcpFallbackToast from './components/TcpFallbackToast';
import MediaHealthToast from './components/MediaHealthToast';
import ControlPanel from './components/ControlPanel';
import LoadingOverlay, { INITIAL_AUTH_MESSAGE } from './components/LoadingOverlay';
import { BrandLogo } from './components/StatusCard';
import DisconnectScreen from './components/DisconnectScreen';

// ----- Incoming file (UE "Send File") UI -----
// A single glass "file transfer" card, bottom-right: shows the file icon (or an
// image thumbnail), name, size and a live progress bar while receiving, then a
// completed state with the right delivery action for the platform.

function spFmtBytes(n) {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
}

function spEnsureFileCardStyles() {
  if (document.getElementById('sp-file-card-styles')) return;
  const s = document.createElement('style');
  s.id = 'sp-file-card-styles';
  s.textContent = `
    @keyframes spCardIn { from { opacity:0; transform:translateY(-16px) scale(.98); } to { opacity:1; transform:none; } }
    #sp-file-card { position:fixed; right:24px; top:24px; z-index:2147483647; width:340px; max-width:calc(100vw - 32px);
      background:rgba(28,28,32,.72); backdrop-filter:blur(24px) saturate(1.5); -webkit-backdrop-filter:blur(24px) saturate(1.5);
      border:1px solid rgba(255,255,255,.12); border-radius:18px; box-shadow:0 8px 32px rgba(0,0,0,.44), 0 1px 0 rgba(255,255,255,.06) inset;
      color:#f4f4f5; font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif; padding:14px 16px; letter-spacing:-.01em;
      animation:spCardIn .28s cubic-bezier(.2,.8,.2,1); box-sizing:border-box; }
    #sp-file-card .sp-row { display:flex; align-items:center; gap:12px; }
    #sp-file-card .sp-icon { width:44px; height:44px; flex:0 0 44px; border-radius:10px; background:rgba(255,255,255,.08);
      display:flex; align-items:center; justify-content:center; font-size:20px; overflow:hidden;
      background-size:cover; background-position:center; }
    #sp-file-card .sp-info { flex:1 1 auto; min-width:0; }
    #sp-file-card .sp-name { font-size:14px; font-weight:600; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
    #sp-file-card .sp-sub { font-size:12px; color:#a1a1aa; margin-top:2px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
    #sp-file-card .sp-close { flex:0 0 auto; background:none; border:none; color:#a1a1aa; font-size:20px; line-height:1;
      cursor:pointer; padding:2px 6px; border-radius:6px; }
    #sp-file-card .sp-close:hover { color:#fff; background:rgba(255,255,255,.08); }
    #sp-file-card .sp-track { height:6px; background:rgba(255,255,255,.12); border-radius:99px; overflow:hidden; margin-top:12px; }
    #sp-file-card .sp-bar { height:100%; width:0%; border-radius:99px; background:#0A84FF; transition:width .2s cubic-bezier(.2,.8,.2,1); }
    #sp-file-card .sp-btn { display:block; width:100%; margin-top:12px; padding:10px 14px; border:none; border-radius:10px;
      background:#0A84FF; color:#fff; font-size:14px; font-weight:600; letter-spacing:-.01em; cursor:pointer; transition:filter .15s ease; }
    #sp-file-card .sp-btn:hover { filter:brightness(1.08); }
    #sp-file-card .sp-btn:active { filter:brightness(.94); }
  `;
  document.head.appendChild(s);
}

function spGetFileCard(create) {
  let el = document.getElementById('sp-file-card');
  if (!el && create) {
    spEnsureFileCardStyles();
    el = document.createElement('div');
    el.id = 'sp-file-card';
    el.innerHTML = `
      <div class="sp-row">
        <div class="sp-icon" data-role="icon">📄</div>
        <div class="sp-info">
          <div class="sp-name" data-role="name"></div>
          <div class="sp-sub" data-role="sub"></div>
        </div>
        <button class="sp-close" data-role="close" aria-label="Dismiss">×</button>
      </div>
      <div class="sp-track" data-role="track"><div class="sp-bar" data-role="bar"></div></div>`;
    document.body.appendChild(el);
    el.querySelector('[data-role=close]').onclick = () => spRemoveFileCard();
  }
  return el;
}

function spRemoveFileCard() {
  const el = document.getElementById('sp-file-card');
  if (!el) return;
  if (el.dataset.thumbUrl) { try { URL.revokeObjectURL(el.dataset.thumbUrl); } catch (e) {} }
  el.remove();
}

// Epic's file-transfer protocol sends only the extension, not a name, so we build
// one. Strip any leading dot on the extension — that is what produced "transfer..png".
function spFileName(f) {
  if (!f) return 'capture';
  // Memoise only the TIMESTAMP per transfer (stable across progress + delivery,
  // reset in the FileExtension handler); recompute the extension every call so the
  // final download name still picks it up even if it landed after the first tick.
  if (!f.spStamp) {
    const d = new Date();
    const p = (n) => String(n).padStart(2, '0');
    f.spStamp = `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
  }
  const ext = String(f.extension || '').replace(/^\.+/, '').trim().toLowerCase();
  return ext ? `capture-${f.spStamp}.${ext}` : `capture-${f.spStamp}`;
}

// Live progress while a transfer is in flight.
function updateFileProgress(received, total, filename, receivedBytes) {
  if (!total) { spRemoveFileCard(); return; }
  const el = spGetFileCard(true);
  if (el.dataset.complete) return; // don't overwrite the completed state
  const pct = Math.min(100, Math.round((received / total) * 100));
  if (filename) el.querySelector('[data-role=name]').textContent = filename;
  el.querySelector('[data-role=sub]').textContent = receivedBytes
    ? `Receiving… ${pct}% · ${spFmtBytes(receivedBytes)}`
    : `Receiving… ${pct}%`;
  el.querySelector('[data-role=bar]').style.width = `${pct}%`;
}

// Transfer complete: switch the card to its finished state and deliver the file.
//  - Desktop: auto-download, then offer a "Download again" button; auto-dismisses.
//  - iOS Safari: it ignores the download attribute and blocks non-gesture
//    downloads, so we show a "Save" button; on tap (a real gesture) we open the
//    native share sheet (Save Image / Save to Files).
function deliverReceivedFile(blob, filename, mimetype) {
  const type = mimetype || 'application/octet-stream';
  const isImage = type.startsWith('image/');
  const el = spGetFileCard(true);
  el.dataset.complete = '1';
  el.querySelector('[data-role=name]').textContent = filename;
  el.querySelector('[data-role=sub]').textContent = `${spFmtBytes(blob.size)} · ready`;

  const icon = el.querySelector('[data-role=icon]');
  if (isImage) {
    const thumbUrl = URL.createObjectURL(blob);
    el.dataset.thumbUrl = thumbUrl;
    icon.style.backgroundImage = `url("${thumbUrl}")`;
    icon.textContent = '';
  } else {
    icon.textContent = '📄';
  }

  const track = el.querySelector('[data-role=track]');
  if (track) track.remove();
  let btn = el.querySelector('.sp-btn');
  if (!btn) { btn = document.createElement('button'); btn.className = 'sp-btn'; el.appendChild(btn); }

  if (isIOS) {
    btn.textContent = `Save ${isImage ? 'image' : 'file'}`;
    btn.onclick = async () => {
      try {
        const file = new File([blob], filename, { type });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: filename });
        } else {
          const url = URL.createObjectURL(blob);
          window.open(url, '_blank');
          setTimeout(() => URL.revokeObjectURL(url), 30000);
        }
      } catch (e) {
        // user cancelled — keep the button so they can retry
      }
    };
  } else {
    const doDownload = () => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    };
    doDownload();
    el.querySelector('[data-role=sub]').textContent = `${spFmtBytes(blob.size)} · downloaded`;
    btn.textContent = 'Download again';
    btn.onclick = doDownload;
    setTimeout(() => spRemoveFileCard(), 10000); // auto-dismiss on desktop
  }
}

// Module-level mutable state — intentional for cross-render access inside stream callbacks
let isMobile = isMobilePhone();
let geoData = {};
let ueMessage = {};
let reconnectWindowStart = null;
let reconnecting = false;
let sfuRecoveryLoop = null;
let psNegotiationListener = null;
let lastNegotiationRecoverAt = 0;
let hostWaitTimer = null;

// The password a viewer typed, kept for THIS TAB only (sessionStorage: gone when the tab closes, never
// shared with other tabs or sites). A page reload — the SFU recovery below does one, so does a viewer
// pressing F5 — re-runs the access check, and without this every reload re-asked for the password
// (Swarnim Evara, 2026-10-09: a guest saw the password dialog every few seconds). A remembered password
// is tried silently; only a rejected one brings the dialog back.
const PW_KEY = (projectId) => `sp:pw:${projectId}`;
const rememberPassword = (projectId, pw) => { try { sessionStorage.setItem(PW_KEY(projectId), pw); } catch (_) { /* storage blocked in a third-party frame */ } };
const rememberedPassword = (projectId) => { try { return sessionStorage.getItem(PW_KEY(projectId)); } catch (_) { return null; } };
const forgetPassword = (projectId) => { try { sessionStorage.removeItem(PW_KEY(projectId)); } catch (_) { /* ignore */ } };
let PixelStreamingApp;
let PixelStreamingUiApp;
let activeResolution;
let resolutionSetting = [];
let message;

let userName = generateRandomName();

let avatar = createUserAvatar(userName);
// The env var is the BARE origin (e.g. https://platform.staging.streampixel.io).
// platform-api serves everything under /api/v1, so the code owns that prefix —
// appended once here, with any trailing slash stripped first, so the Vercel value
// stays a clean origin (no //, no missing-/api/v1 footgun). This base is also
// handed to the voice-chat SDK as apiBase, which expects the /api/v1 base too.
const API_URL = process.env.REACT_APP_PLATFORM_API_URL
  ? `${process.env.REACT_APP_PLATFORM_API_URL.replace(/\/+$/, '')}/api/v1`
  : undefined;

// Local dev mode no longer needs a frontend signalling-host env var: the dev
// ticket response carries config.signallingHost (the backend is the single
// source), read off `project` in startPlay exactly like a normal stream's host.

// The hosts the player is served from. Anything else means the page is being
// served on a CUSTOMER's domain, which identifies their project.
//
// main hardcoded `host.endsWith('streampixel.io')`. That is one deployment's
// domain: on-prem serves the player from the customer's own, so every page load
// there looks like a custom domain and triggers a lookup that cannot succeed.
// Configurable, with the SaaS value as the default so nothing changes here.
const PLAYER_DOMAINS = (process.env.REACT_APP_PLAYER_DOMAINS || 'streampixel.io')
  .split(',').map((d) => d.trim().toLowerCase()).filter(Boolean);
// Bare origin (e.g. https://telemetry.staging.streampixel.io); append the ingest
// path here. Logs + WebRTC diagnostics are POSTed to this exact URL.
const LOGGER_URL = process.env.REACT_APP_TELEMETRY_URL
  ? `${process.env.REACT_APP_TELEMETRY_URL.replace(/\/+$/, '')}/api/frontend-streaming-logs`
  : undefined;

const isCustomDomain = (host) =>
  Boolean(host) && !PLAYER_DOMAINS.some((d) => host === d || host.endsWith(`.${d}`));

/**
 * STARTED AT MODULE LOAD, not inside the effect that needs it.
 *
 * On a custom domain the project id is unknown until this returns, so it sits
 * directly in front of everything: no stream can start without it. Waiting for
 * React to mount before ASKING adds the whole mount to that delay for no reason —
 * the host is known the instant this file is parsed, and the network is idle then.
 *
 * Kicking it off here overlaps the request with React mounting, so by the time the
 * effect wants an id the answer has usually arrived. It is a promise, not a value:
 * the effect awaits it and gets whatever state it is in.
 *
 * Deliberately NOT an edge rewrite. That version was faster still and tied the
 * feature to one host — it cannot work on-prem, and it bills per request. This
 * costs nothing, runs anywhere the player runs, and gives up only the milliseconds
 * between page load and script parse.
 *
 * Failures are swallowed to null. The caller decides what to do about it, and an
 * unhandled rejection at module scope would surface as a console error on every
 * custom-domain load.
 */
/**
 * Retry the two calls a viewer CANNOT start a stream without — the access
 * descriptor and the admission ticket.
 *
 * Neither retried, so a single transient failure ended the session before it
 * began: the viewer saw a dead page, not a slow one. That window is real and
 * routine — the load balancer briefly has no healthy backend whenever a pod is
 * replaced, which happens on every deploy AND on Autopilot's own eviction
 * schedule, and the resulting 502 arrives at the exact moment someone is
 * pressing play.
 *
 * ONLY TRANSPORT-LEVEL FAILURES are retried: a network error (status 0) or
 * 502/503/504. Everything else is the server's considered answer and must
 * reach the caller unchanged — retrying a 403 would fight the password gate,
 * and retrying a 429 would deepen the very rate-limit it reports.
 *
 * Safe for the ticket POST: a request that never landed minted nothing, and one
 * whose response was lost minted a short-lived ticket nobody holds. Neither
 * creates a session — that happens later, over the signalling connection.
 *
 * Three attempts over ~1.2s total, which covers a pod swap without making a
 * genuinely down backend feel like a hang.
 */
const TRANSIENT_STATUSES = new Set([502, 503, 504]);
const withTransientRetry = async (fn, attempts = 3) => {
  for (let i = 0; ; i++) {
    try {
      return await fn();
    } catch (error) {
      const status = error?.response?.status || 0;
      const transient = status === 0 || TRANSIENT_STATUSES.has(status);
      if (!transient || i >= attempts - 1) throw error;
      console.warn(`[startup] transient ${status || 'network'} error — retry ${i + 1}/${attempts - 1}`);
      await new Promise((r) => setTimeout(r, 300 * (i + 1)));
    }
  }
};

const domainLookup = (() => {
  try {
    const host = window.location.hostname.toLowerCase();
    if (!API_URL || !isCustomDomain(host)) return null;
    // The ACCESS DESCRIPTOR, not just an id. This called /projects/by-domain,
    // whose only output was a project id — the player then had to call
    // /stream/access with that id before it could paint anything, two sequential
    // round trips on exactly the pages a customer paid to brand. The descriptor
    // already carries projectId, so one call now answers both questions.
    // Retried for the same reason as the calls in authenticate(): on a custom
    // domain this is the FIRST request and the only source of the projectId, so
    // one transient 502 during a pod swap leaves the viewer with no project at
    // all. A 404 (domain genuinely not ours) still resolves null immediately —
    // only transport failures are worth asking twice about.
    const attempt = (i) =>
      fetch(`${API_URL}/stream/access/by-domain/${encodeURIComponent(host)}`)
        .then((r) => {
          if (r.ok) return r.json();
          if ((r.status === 502 || r.status === 503 || r.status === 504) && i < 2) {
            return new Promise((res) => setTimeout(res, 300 * (i + 1))).then(() => attempt(i + 1));
          }
          return null;
        })
        .catch(() => (i < 2
          ? new Promise((res) => setTimeout(res, 300 * (i + 1))).then(() => attempt(i + 1))
          : null));
    return attempt(0);
  } catch (_e) {
    return null;
  }
})();

let maxHeight;
let maxWidth;
let roomName;
let sessionToken;
let telemetryToken; // session-scoped token for posting telemetry to the logger
// Session-scoped token exchanged at /liveKit/getToken for a room grant.
//
// MODULE SCOPE, like telemetryToken — assigned when the ticket resolves. Assigning
// it does NOT re-render React, so it must never be captured as a plain prop value:
// anything constructed before the ticket lands would keep `undefined` forever.
// That is precisely what shipped — every getToken went out with no Authorization
// header while apiBase (a const) arrived fine. It is passed as a GETTER instead,
// and read at request time.
let voiceToken;
let urlPassword;
// Why the ticket request failed, so the caller can tell a wrong password (403)
// apart from a brute-force lockout (429). fetchData returns a bare boolean, so
// without this every failure rendered as "Incorrect password" — including the
// lockout, which left a viewer retrying a password that was already correct and
// never learning they had to wait. Module-scope (like the tokens above) is safe
// here because it is read synchronously right after the await, then fed into
// React state; nothing renders off it directly.
let lastTicketError = null;

// Where the project wants its logo. Returns only the EDGE OFFSETS — the element
// that renders it owns position and z-index. This was duplicated at three call
// sites that each baked in a different stacking value (10000000, 100000000 and
// 1), so the logo's depth depended on which path had run last, and the 1 put it
// behind the overlay entirely.
const LOGO_OFFSETS = {
  TopLeft: { top: '12px', left: '12px' },
  TopRight: { top: '12px', right: '12px' },
  BottomLeft: { bottom: '12px', left: '12px' },
  BottomRight: { bottom: '12px', right: '12px' },
};
const logoOffsets = (position) => LOGO_OFFSETS[position] || null;

// The broker's ssoError codes, said in words a viewer can act on. Anything
// unrecognised falls back to generic wording rather than leaking a raw code —
// the codes are deliberately coarse so the IdP's own error text never reaches
// the browser.
const SSO_ERRORS = {
  access_denied: 'Your account does not have access to this stream.',
  connection_mismatch: 'Single sign-on is misconfigured for this stream. Contact the owner.',
  connection_missing: 'Single sign-on is not set up for this stream. Contact the owner.',
  project_missing: 'This stream is no longer available.',
  expired_state: 'That sign-in link expired. Please try again.',
  invalid_state: 'That sign-in attempt could not be verified. Please try again.',
  missing_code: 'Your identity provider did not complete the sign-in. Please try again.',
  idp_error: 'Your identity provider rejected the sign-in. Please try again.',
  state_unavailable: 'Sign-in is temporarily unavailable. Please try again in a moment.',
  sso_failed: 'Sign-in failed. Please try again.',
  sso_start_failed: 'Sign-in could not be started. Please try again.',
};
const ssoErrorMessage = (code) => SSO_ERRORS[code] || 'Sign-in failed. Please try again.';

// "1 minute" / "12 minutes" / "45 seconds" — a lockout measured in raw seconds
// reads like a machine talking.
const formatWait = (secs) => {
  if (secs < 60) return `${secs} second${secs === 1 ? '' : 's'}`;
  const mins = Math.ceil(secs / 60);
  return `${mins} minute${mins === 1 ? '' : 's'}`;
};
let urlSsoGrant;

// UE reports VideoEncoderAvgQP on the codec's native scale: H.264/H.265 use
// 0-51 but AV1 uses qindex 0-255, so a flawless AV1 stream would read as
// "Poor" against H.264-calibrated bands. Map AV1 onto the H.264 scale before
// the value reaches the quality indicator.
function normalizeQpToH264Scale(qp, codec) {
  if (qp <= 0) return qp;
  if ((codec || '').toUpperCase() === 'AV1') return Math.round(qp * (51 / 255));
  return qp;
}

function applyCodecQpOverride(stream, selectedCodec, primaryCodec) {
  if (!stream || !selectedCodec || !primaryCodec) return;
  if (selectedCodec.toUpperCase() === primaryCodec.toUpperCase()) return;
  const qp = QP_BY_CODEC[selectedCodec.toUpperCase()];
  if (!qp) return;
  setTimeout(() => {
    const handler = stream?._webRtcController?.streamMessageController?.toStreamerHandlers?.get('Command');
    if (!handler) return;
    handler([JSON.stringify({ 'Encoder.MinQP': qp.min })]);
    handler([JSON.stringify({ 'Encoder.MaxQP': qp.max })]);
  }, 2000);
}

// Whether ANY chat feature is enabled — the single source of truth for the
// "should we join the LiveKit room" decision (text chat sends over the room's
// data channel, voice/video send media, so any one of them needs the room).
// videoChat defaults OFF when unset, matching the VoiceChatUI prop default.
// NOTE: this is only the CONNECT decision — which UI to show is decided
// per-feature downstream (ChatPanel/VideoTilesUI/ControlPanel each check their
// own flag), not here.
const isChatEnabled = (p) => !!(p?.textChat || p?.voiceChat || (p?.videoChat ?? false));

const webIp =  process.env.REACT_APP_WEBIP;

const App = () => {
  const videoRef = useRef(null);
  const logStatsTimerRef = useRef(null);
  const logStatsProbeRef = useRef(null);
  // Media health monitor (utils/mediaHealth): started on WebRTC connected for
  // direct sessions, stopped on any disconnect. The two flag refs feed its
  // guards from the player's own lifecycle events.
  const mediaHealthRef = useRef(null);
  const playRejectedRef = useRef(false);
  const videoInitRef = useRef(false);
  const resizeTimerRef = useRef(null);
  const editTextModalRef = useRef(null);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMuted, setIsMuted] = useState(true);  // stream audio muted by default; user unmutes manually
  const [keyStates, setKeyStates] = useState({ t: false, five: false });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState(null);
  const [isStreamConnect, setIsStreamConnect] = useState(true);
  const [newMessage, setNewMessage] = useState(0);
  const [passwordValid, setPasswordValid] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [sdkConnect, setSdkConnect] = useState(false);
  const [reconnect, setReconnect] = useState(false);
  const [showEnterPassword, setShowEnterPassword] = useState(false);
  const [showSsoLogin, setShowSsoLogin] = useState(false);
  const [enteredPassword, setEnteredPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  // Epoch ms the brute-force lockout clears, 0 when not locked. Drives the
  // disabled state on the gate so a locked-out viewer cannot keep submitting.
  const [lockedUntil, setLockedUntil] = useState(0);
  // Why the last sign-in attempt failed, shown on the SSO card.
  const [ssoError, setSsoError] = useState('');
  // Set when the bootstrap stopped and the viewer is not getting a stream:
  // { title, message }. Takes precedence over every other overlay state.
  const [unavailable, setUnavailable] = useState(null);
  const [project, setProject] = useState();
  const [branding, setBranding] = useState({});
  const [loading, setLoading] = useState(true);
  const [fetchDone, setFetchDone] = useState(false);
  const [resolutionMode, setResolutionMode] = useState();
  const [roomDisconnect, setRoomDisconnect] = useState(false);
  const [toggleMicChat, setToogleMicChat] = useState(false);
  const [roomConnect, setRoomConnect] = useState(false);
  const [xrSupport, setXRSupport] = useState(false);
  const [startChatIf, setStartChatIf] = useState();
  const [authMessage, setAuthMessage] = useState(INITIAL_AUTH_MESSAGE);
  const [negotiationStatus, setNegotiationStatus] = useState('');
  const [showTcpFallbackToast, setShowTcpFallbackToast] = useState(false);
  const [showMediaHealthToast, setShowMediaHealthToast] = useState(false);
  const [primaryCodec, setPrimaryCodec] = useState();
  const [fallbaCodec, setFallbaCodec] = useState();
  const [showUiBtn, setShowUiBtn] = useState(false);
  const [sfuChatActive, setSfuChatActive] = useState(false);
  const [showChatUI, setShowChatUI] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [showChatUiMobile, setShowChatUiMobile] = useState(!isMobile);
  const [showChatUiDesktop, setShowChatUiDesktop] = useState(false);
  const [someoneTyping, setSomeoneTyping] = useState(false);
  const [avgQp, setAvgQp] = useState(-1);
  const [projectId, setProjectId] = useState(null);
  // null = no logoPosition chosen; the renderer then pins the corner default.
  // This used to initialise to {}, which is TRUTHY, so the `|| fallback`
  // below kept the empty object and the logo got `position: fixed` with no
  // top/left/right/bottom at all — planted wherever static flow left it.
  const [logoStyles, setLogoStyles] = useState(null);
  const [sfuHost, setSfuHost] = useState("false");
  const [sfuPlayer, setSfuPlayer] = useState("false");
  const sfuPlayerRef = useRef(false);
  // Local dev mode: the /dev/<devSessionId> route. The customer's own Unreal
  // Editor is the streamer. It runs the EXACT normal-player flow — same access
  // descriptor, same password/SSO gate — differing only in which endpoint mints
  // the ticket (/dev-sessions/:id/ticket) and that the build/live preconditions
  // are skipped (the editor is the source, so there is no uploaded build to
  // check). A streamer restart (4006) is treated as reconnectable. A ref mirrors
  // the state so the once-registered close handler reads the current value.
  const [devMode, setDevMode] = useState(false);
  const devModeRef = useRef(false);
  // The dev session id (dev-xxxxxx) from the /dev/<id> path, kept alongside the
  // real projectId: the ticket call targets the dev endpoint by this id and the
  // player subscribes to this streamer. Null for a normal viewer.
  const devSessionIdRef = useRef(null);
  // Pinned test session (2026-10-12): share/<projectId>?pin=<pinId>. The pin (created by an operator on the ops
  // dashboard) is sent with the ticket mint instead of a password; the backend answers with the session id it
  // recorded the pin under, and the SDK puts that id on the signalling URL so the session lands on the pinned host.
  const pinIdRef = useRef(null);
  const pinnedSessionIdRef = useRef(null);
  sfuPlayerRef.current = (sfuPlayer === "true" || sfuPlayer === true);
  const [streamerId, setStreamerId] = useState(uuidv7());
  const [disconnectMessage, setDisconnectMessage] = useState('');

  // Live mirror of roomConnect so the comms helpers — called from the
  // once-registered stream response handler — read the CURRENT value instead
  // of a stale closure capture.
  const roomConnectRef = useRef(false);
  roomConnectRef.current = roomConnect;
  // Holds the pending disconnect teardown timer so a subsequent connect can
  // cancel it (otherwise the deferred teardown would clobber the reconnect).
  const disconnectTimerRef = useRef(null);

  const openChatUiMobile = () => setShowChatUiMobile(true);
  const closeChatUiMobile = () => setShowChatUiMobile(false);
  const openChatUiDesktop = () => setShowChatUiDesktop(true);
  const closeChatUiDesktop = () => setShowChatUiDesktop(false);
  const handleMicChat = () => setToogleMicChat(prev => !prev);

  const loadingText = (stage) => {
    if (sfuPlayerRef.current) return SFU_LOADING_TEXT[stage];
    return {
      one:   branding.loadingTextOne,
      two:   branding.loadingTextTwo,
      three: branding.loadingTextThree,
    }[stage];
  };

   const startSsoLogin = () => {
    setSsoError('');   // a fresh attempt shouldn't carry the last one's error
    // The normal player carries projectId in the PATH, so a pathname-only returnTo
    // preserves it across the SSO round trip. Dev carries it in ?p=, which pathname
    // drops — so re-add it for dev, else the post-SSO reload cannot bootstrap. The
    // broker appends ?ssoGrant via URLSearchParams, so an existing query is kept.
    const base = window.location.origin + window.location.pathname;
    const returnTo = devModeRef.current ? `${base}?p=${encodeURIComponent(projectId)}` : base;
    window.location.href = `${API_URL}/stream/sso/${projectId}/start?returnTo=${encodeURIComponent(returnTo)}`;
  };

  /**
   * Which project does this hostname belong to?
   *
   * GET /stream/access/by-domain/:domain returns the same descriptor the id route
   * does — projectId, mode, status, ready, branding — so the pre-auth screen can be
   * painted from this one call. It matches on a VERIFIED custom domain, so pointing
   * DNS at us does not claim someone else's project.
   *
   * Uses API_URL rather than a literal host. main hardcoded
   * https://api.streampixel.io, which meant staging could never resolve a domain
   * and an on-prem deployment called the vendor's control plane. It also used the
   * /pixelStripeApi prefix, which now survives only for the API-key surface.
   */
  const resolveProjectByDomain = async (fallbackId) => {
    try {
      // AWAITS THE REQUEST ALREADY IN FLIGHT — see `domainLookup` at module scope.
      // Starting a second one here would throw away the head start and double the
      // load on an endpoint every viewer of a custom domain hits.
      const access = domainLookup ? await domainLookup : null;
      const id = access && access.projectId;
      if (!id) throw new Error('no project for this domain');
      // No parentUrl state to set — main kept the resolved host in state, this
      // branch derives embedderOrigin from getParentFrameUrl() where it is used.
      // Setting it here would be a ReferenceError, and the domain is not needed
      // again once it has produced an id.
      setProjectId(id);
    } catch (error) {
      // FALLS BACK TO THE PATH ID, which is the behaviour worth keeping from main.
      // A domain lookup failing does not mean the link is bad: the API may be
      // briefly unreachable, and a custom domain can carry a project id in its path
      // as well. Treating every failure as fatal turns a transient blip into a dead
      // player for every viewer on that domain at once.
      console.error('Could not resolve a project for this domain:', error);
      if (fallbackId) {
        setProjectId(fallbackId);
        return;
      }
      // A 404 (nobody has verified this domain) and a network failure are shown
      // identically on purpose. A viewer can act on neither, and distinguishing
      // them would tell an unauthenticated stranger which domains are registered.
      setStatus('invalidurl');
      setUnavailable({
        title: 'This stream is unavailable',
        message: 'The link may be incorrect, expired, or no longer active.',
      });
    }
  };

  useEffect(() => {
    const pathSegments = window.location.pathname.split('/').filter(Boolean);
    const baseId = pathSegments[pathSegments.length - 1];
    const isDev = pathSegments[0] === 'dev' && pathSegments.length >= 2;
    const urlSearchParams = new URLSearchParams(window.location.search);
    const pwdValue = urlSearchParams.get('pwd');
    const ssoGrantValue = urlSearchParams.get('ssoGrant');
    // The broker sends the viewer back with ?ssoError=<code> when sign-in fails.
    // Nothing read it, so a rejected login landed back on the "Sign in" button
    // with no explanation and the viewer simply clicked it again.
    const ssoErrorValue = urlSearchParams.get('ssoError');
    const pinValue = urlSearchParams.get('pin');
    if (pinValue && /^pin-[a-f0-9]{24}$/.test(pinValue)) pinIdRef.current = pinValue;
    if (ssoErrorValue) setSsoError(ssoErrorMessage(ssoErrorValue));

    for (const [key, value] of urlSearchParams.entries()) {
      // Auth params are never forwarded into the Unreal message payload. `p` is
      // the dev link's projectId (reserved), so it is excluded on the dev route.
      if (key !== 'pwd' && key !== 'ssoGrant' && key !== 'ssoError' && key !== 'pin' && !(isDev && key === 'p')) ueMessage[key] = value;
      if (key === 'streamerId') setStreamerId(value);
      if (key === 'sfuHost') setSfuHost(value);
      if (key === 'sfuPlayer') setSfuPlayer(value);
    }

    // Password / SSO-grant from the URL, captured BEFORE the dev branch so both
    // the normal and dev flows pick them up (the dev branch returns early). The
    // gate in authenticate()/fetchData() consumes whichever is present.
    if (pwdValue && baseId) {
      urlPassword = pwdValue;
      setEnteredPassword(pwdValue);
    }
    if (ssoGrantValue && baseId) {
      urlSsoGrant = ssoGrantValue;
    }

    // LOCAL DEV MODE: /dev/<devSessionId>?p=<projectId>. The customer's own Unreal
    // Editor is the streamer, viewed through the SAME player flow as a published
    // build — no synthesized config, no pre-shared token. We only capture the two
    // ids here and hand off to the normal authenticate() by setting projectId; the
    // dev-specific parts (skip the build/live preconditions, mint the ticket at
    // /dev-sessions/:id/ticket, subscribe to devSessionId) are small branches keyed
    // off devModeRef inside that shared path. The ticket is issued AFTER the
    // password/SSO gate, exactly like the normal player.
    if (isDev) {
      devModeRef.current = true;
      setDevMode(true);
      const devSessionId = baseId; // dev-xxxxxx
      const pid = urlSearchParams.get('p');
      if (!pid) {
        setStatus('invalidurl');
        setUnavailable({
          title: 'This dev link is incomplete',
          message: 'Start the session again from the dashboard.',
        });
        return;
      }
      devSessionIdRef.current = devSessionId;
      setStreamerId(devSessionId); // the dev editor registers under devSessionId
      setProjectId(pid);           // drives authenticate() exactly like a normal link
      return;
    }

    showLoadingSpinner();

    // SERVED FROM A HOST THAT IS NOT OURS MEANS A CUSTOM DOMAIN.
    //
    // 41 projects work this way: DNS for play.theirbrand.com points at the player,
    // so window.location.host IS the customer's domain and identifies the project.
    // The id may be absent from the path entirely.
    //
    // The trigger is the HOST, not a missing id — a custom domain can carry a path
    // too. And the host comes from window.location, NOT the parent frame: the page
    // is being served on that domain, so its own location is the authority. Reading
    // the embedder would answer a different question.
    //
    // This existed on main and a "remove unused files" refactor dropped it, turning
    // all 41 into "This stream is unavailable" — indistinguishable from a genuinely
    // broken link, which is why it could go unnoticed.
    if (isCustomDomain(window.location.hostname.toLowerCase())) {
      resolveProjectByDomain(baseId);
      return;
    }

    if (!baseId) {
      setStatus('invalidurl');
      setUnavailable({
        title: 'This stream is unavailable',
        message: 'The link may be incorrect, expired, or no longer active.',
      });
      return;
    }

    setProjectId(baseId);
  }, []);

  useEffect(() => {
    const handleClickOutside = () => {
      if (isModalVisible) setIsModalVisible(false);
    };
    if (isModalVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isModalVisible]);

  const getStats = () => {
    const statsElement = PixelStreamingUiApp?.statsPanel?._statsResult;
    if (!statsElement) return {};
    const statDivs = statsElement.querySelectorAll('div');
    if (!statDivs.length) return {};

    const result = {};
    statDivs.forEach((div) => {
      const text = div.textContent.trim();
      if (!text) return;
      const colonIdx = text.indexOf(':');
      if (colonIdx === -1) return;
      const key = text.slice(0, colonIdx).trim();
      const value = text.slice(colonIdx + 1).trim();
      if (key && value && key.toLowerCase() !== 'players') {
        result[key] = value;
      }
    });
    return result;
  };

  // While the stats modal is open, refresh the scraped stats once per second so
  // the panel updates live instead of showing a frozen snapshot from open-time.
  useEffect(() => {
    if (!isModalVisible) return;
    setStats(getStats());
    const id = setInterval(() => setStats(getStats()), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalVisible]);

  useEffect(() => {
    // Both normal and dev viewers run authenticate(): dev sets projectId (from ?p=)
    // in the bootstrap above and then takes the same access → gate → ticket path,
    // branching only on devModeRef where it must.
    if (projectId) authenticate();
  }, [projectId]);

  // End the bootstrap with a reason the viewer can read, and make sure nothing
  // downstream keeps spinning: the loading UI, the password gate and the SSO card
  // are all cleared, so exactly one thing is on screen.
  const stopWith = (title, message) => {
    setUnavailable({ title, message });
    setShowEnterPassword(false);
    setShowSsoLogin(false);
    setLoading(false);
    hideLoadingSpinner();
    window.parent.postMessage({ type: "stream-state", value: "unavailable", title, message }, "*");
  };

  const authenticate = async () => {
    try {
      // REUSES THE DESCRIPTOR ALREADY FETCHED on a custom domain, instead of
      // asking for the same object a second time.
      //
      // Without this the merge saves nothing: the by-domain call returns the full
      // descriptor and then this would fetch it again by id — two round trips,
      // which is precisely what collapsing the endpoints was meant to remove.
      //
      // The projectId comparison is what makes reuse safe. It is the id the
      // descriptor itself reported, so the two can only differ if something
      // reassigned projectId in between, and falling through to a fresh fetch is
      // the right answer then.
      const prefetched = domainLookup ? await domainLookup : null;
      const data = (prefetched && prefetched.projectId === projectId)
        ? prefetched
        : (await withTransientRetry(() => axios.get(`${API_URL}/stream/access/${projectId}`))).data;

      // `ready` was returned by /stream/access all along and never read here, so
      // a project with no published build still got a password prompt, an SSO
      // round trip, or a ticket request — all of which can only end in a 409.
      const { mode, status: projectStatus, ready, branding: brandingData = {} } = data;

      // Branding first: whatever we show next — a gate, "offline", "no build" —
      // should already be wearing the customer's logo and banner.
      setBranding(brandingData);
      // Dev previews the editor regardless of the project's live switch or whether
      // a build was ever published, so it is always "active" and skips the two
      // preconditions below. Everything else — branding, the gate — is identical.
      setStatus((devModeRef.current || projectStatus) ? 'active' : 'inactive');
      if (brandingData.theme === "Dark") setDarkMode(true);

      setLogoStyles(logoOffsets(brandingData.logoPosition));

      // Two preconditions, in order, before anything else happens. Neither was
      // checked here: a switched-off or buildless project still got a password
      // prompt or a full SSO round trip, and the viewer only discovered it was
      // pointless after authenticating — the ticket call is the thing that 409s.
      // Dev skips BOTH: the customer's editor is the source, so there is no live
      // switch to honour and no uploaded build to require.
      //
      // 1. Is the stream switched on at all?
      if (!devModeRef.current && projectStatus !== true) {
        stopWith('This stream is offline', 'The project owner has turned it off. Check back later.');
        return;
      }
      // 2. Is there a build to stream? `ready` is hasValidApp() server-side.
      if (!devModeRef.current && ready === false) {
        stopWith('This stream is unavailable', 'No live build has been published yet.');
        return;
      }

      if (mode === "password") {
        const remembered = urlPassword || rememberedPassword(projectId);
        if (remembered) {
          handlePasswordSubmit(null, remembered, { silent: !urlPassword });
        } else {
          message = { type: "stream-state", value: "showPassword" };
          window.parent.postMessage(message, "*");
          setShowEnterPassword(true);
          setLoading(false);
        }
      } else if (mode === "sso") {
        if (urlSsoGrant) {
          const grant = urlSsoGrant;
          urlSsoGrant = undefined;
          const success = await fetchData({ ssoGrant: grant });
          if (success) {
            setShowSsoLogin(false);
            setLoading(true);
            return;
          }
          // The grant is single-use and short-lived, so the usual causes are a
          // reloaded callback URL or a stale link. Say that, instead of dropping
          // the viewer back on the button with no explanation.
          setSsoError(
            lastTicketError?.status === 503
              ? 'Sign-in is temporarily unavailable. Please try again in a moment.'
              : 'That sign-in link has already been used or expired. Please sign in again.'
          );
        }
        message = { type: "stream-state", value: "showSsoLogin" };
        window.parent.postMessage(message, "*");
        setShowSsoLogin(true);
        setLoading(false);
        hideLoadingSpinner();
      } else {
        fetchData();
      }
    } catch (error) {
      console.error('Error fetching stream access:', error);
      stopWith(
        'This stream is unavailable',
        'The link may be incorrect, expired, or no longer active.'
      );
    }
  };

  const fetchData = async (extra = {}) => {
    const { projectId: overrideId, ...payload } = extra;
    const appId = overrideId || projectId;

    if (!appId) {
      console.error('fetchData called before projectId was resolved');
      stopWith('This stream is unavailable', 'The link may be incorrect, expired, or no longer active.');
      return false;
    }

    // When this page is IFRAMED on a customer's site, the signalling WebSocket
    // carries OUR origin, so a domain-restricted project would deny its own
    // legitimate embed. Tell the mint who embeds us: ancestorOrigins (the
    // top-level site; read-only, the embedder cannot forge it) with the
    // referrer's origin as the Firefox fallback. The backend signs it into the
    // ticket only when this request really comes from our player origin, and
    // signalling honours it only against the project's own allow-list — a page
    // not on that list gains nothing by embedding us.
    let embedOrigin;
    if (window.self !== window.top) {
      try {
        const ancestors = window.location.ancestorOrigins;
        embedOrigin = ancestors && ancestors.length
          ? ancestors[ancestors.length - 1]
          : (document.referrer ? new URL(document.referrer).origin : undefined);
      } catch { /* leave undefined — behaves exactly as before this feature */ }
    }

    // Dev mints its ticket at the dev endpoint — same gate (public / password /
    // SSO), no build/live precondition — and subscribes to devSessionId rather
    // than the project's regional streamer. embedOrigin is a build-only concern,
    // so it is omitted for dev. Everything downstream reads the same { config }.
    const ticketUrl = devModeRef.current
      ? `${API_URL}/dev-sessions/${devSessionIdRef.current}/ticket`
      : `${API_URL}/stream/ticket`;

    try {
      const response = await withTransientRetry(
        () => axios.post(ticketUrl, {
          projectId: appId,
          ...(embedOrigin && !devModeRef.current ? { embedOrigin } : {}),
          ...(pinIdRef.current && !devModeRef.current ? { pinId: pinIdRef.current } : {}),
          ...payload,
        })
      );

      const { ticket, telemetryToken: telToken, voiceToken: vToken, config } = response.data;
      // Only a pinned mint carries sessionId; it is the key the scheduler reads, so it must reach the SDK unchanged.
      pinnedSessionIdRef.current = (pinIdRef.current && typeof response.data.sessionId === 'string') ? response.data.sessionId : null;
      telemetryToken = telToken; // sent with each telemetry post to the logger
      // Absent when the project has no chat enabled — the backend only mints it
      // then, so undefined here means "chat off", not a failure.
      voiceToken = vToken;

      console.log("CONFIG:",config);

      if (!config || config.message === 'inValidApp') {
        // authenticate() checks `ready` up front, so reaching here means the
        // build was withdrawn between that call and this one.
        stopWith('This stream is unavailable', 'No live build has been published yet.');
        setLoading(false);
        return false;
      }


      sessionToken = ticket;

      const {
        primaryCodec, fallbaCodec, resolution, resolutionMode,
        maxStreamQuality, startResolution, startResolutionTab,
        startResolutionMobile,
        startChat,
      } = config;
      // signallingHost is deliberately NOT destructured here: the value is read
      // straight off `project` (the same config object) where the player is
      // constructed, and a second binding only invited the two to diverge.


      roomName = appId;

      setStartChatIf(startChat);
      setProject(config);
      setResolutionMode(resolutionMode);
      setFetchDone(true);
      setPrimaryCodec(primaryCodec);
      setFallbaCodec(fallbaCodec);


      const resIndex = resolutionDef.findIndex(r => r === maxStreamQuality);
      resolutionSetting = resolutionDef.slice(0, resIndex + 1);

      const getResolutionData = (resString) => {
        const match = resString?.match(/\((\d+x\d+)\)/);
        if (!match) return null;
        const [width, height] = match[1].split('x');
        return { resolution: resString, width, height };
      };

      let resData;
      if (isMobile) resData = getResolutionData(startResolutionMobile);
      else if (isTablet) resData = getResolutionData(startResolutionTab);
      else if (isDesktop) resData = getResolutionData(startResolution);

      if (resData) {
        activeResolution = resData.resolution;
        maxWidth = resData.width;
        maxHeight = resData.height;
      }

      if (resolution === false) {
        const videoElement = document.querySelector('video');
        if (videoElement) videoElement.style.objectFit = 'contain';
      }

      return true;
    } catch (error) {
      console.error('Error generating stream ticket:', error);
      // Keep the status/message so the password gate can distinguish 403 from
      // 429; `status: 0` means the request never reached the server.
      lastTicketError = {
        status: error?.response?.status || 0,
        message: error?.response?.data?.message || '',
        // Seconds until a brute-force lockout clears; 0 when unknown.
        retryAfter: Number(error?.response?.data?.retryAfter) || 0,
        // true only at the hard cap, where waiting really is the only option.
        hard: error?.response?.data?.hard === true,
      };
      // The SSO branch sets its own, more specific message after this returns.
      if (!payload.password && !payload.ssoGrant) {
        stopWith('This stream is unavailable', 'The link may be incorrect, expired, or no longer active.');
      }
      return false;
    }
  };

  // Tick the lockout down so the gate re-enables itself the moment the window
  // passes — without this the viewer has to reload to discover it expired.
  useEffect(() => {
    if (!lockedUntil) return undefined;
    const id = setInterval(() => {
      const left = Math.ceil((lockedUntil - Date.now()) / 1000);
      if (left <= 0) {
        setLockedUntil(0);
        setPasswordError('');
      } else {
        setPasswordError(`Too many attempts. Try again in ${formatWait(left)}.`);
      }
    }, 1000);
    return () => clearInterval(id);
  }, [lockedUntil]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key.toLowerCase() === 't') setKeyStates(prev => ({ ...prev, t: true }));
      if (event.key === '5') setKeyStates(prev => ({ ...prev, five: true }));
    };
    const handleKeyUp = (event) => {
      if (event.key.toLowerCase() === 't') setKeyStates(prev => ({ ...prev, t: false }));
      if (event.key === '5') setKeyStates(prev => ({ ...prev, five: false }));
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      try { mediaHealthRef.current?.stop(); } catch (_) { /* ignore */ }
    };
  }, []);

  useEffect(() => {
    if (keyStates.t && keyStates.five && PixelStreamingUiApp) {
      setStats(getStats());
      setIsModalVisible(prev => !prev);
    }
  }, [keyStates]);

  const handleResize = useCallback(() => {
    clearTimeout(resizeTimerRef.current);
    resizeTimerRef.current = setTimeout(() => {
      let viewportH = Math.floor(window.innerHeight * window.devicePixelRatio);
      let viewportW = Math.floor(window.innerWidth * window.devicePixelRatio);
      let divideNumber = Math.min(maxWidth / viewportW, maxHeight / viewportH, 1);
      let resW = Math.floor(divideNumber * viewportW);
      let resH = Math.floor(divideNumber * viewportH);
      if (resW % 2 !== 0) resW -= 1;
      if (resH % 2 !== 0) resH -= 1;
      handleResolution(`${resW}x${resH}wf`);
    }, 250);
  }, []);

  useEffect(() => {
    if (resolutionMode === "Dynamic Resolution Mode") {
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
        clearTimeout(resizeTimerRef.current);
      };
    }
  }, [resolutionMode, handleResize]);

  const handleResolution = (resolution) => {
    if (PixelStreamingUiApp) {
      PixelStreamingUiApp.stream.emitConsoleCommand(`r.SetRes ${resolution}`);
    }
  };

  useEffect(() => {
    if (!showEnterPassword && status === "active" && fetchDone) {
      const timeoutId = setTimeout(() => {
        setAuthMessage(loadingText('one'));
        message = { type: "stream-state", value: "connecting" };
        window.parent.postMessage(message, "*");
        startPlay();

        if (sfuPlayerRef.current) {
          clearTimeout(hostWaitTimer);
          hostWaitTimer = setTimeout(() => {
            setAuthMessage("Waiting for the host to join…");
          }, HOST_WAIT_MS);
        }
      }, 1000);
      return () => { clearTimeout(timeoutId); clearTimeout(hostWaitTimer); };
    }
  }, [status, showEnterPassword, fetchDone]);

  // Logger posts carry the session-scoped telemetry token so the (public,
  // token-gated) logger ingest accepts them.
  // Stable across renders (empty deps) so it never churns the effect that builds
  // the chat SDK, and it closes over MODULE scope — so calling it returns whatever
  // `voiceToken` holds NOW, not what it held when this component last rendered.
  const getVoiceToken = useCallback(() => voiceToken, []);

  const loggerPostOpts = () => ({
    timeout: 1500,
    headers: telemetryToken ? { Authorization: `Bearer ${telemetryToken}` } : {},
  });

  function logStatsStart(loadingText, diagnostics = {}) {
    const data = {
      streamerId,
      projectId,
      projectRegion: project.region,
      loadingText,
      time: new Date().toISOString(),
      geoData,
      os: osName,
      browserName,
      // Embed-aware: the customer's page when iframed, our own URL otherwise.
      domainUrl: getAttributionUrl(),
      // Keep the share URL visible when embedded so both sides are traceable.
      ...(window.self !== window.top ? { shareUrl: window.location.href } : {}),
      sdk: false,
      ...diagnostics,
    };
    axios.post(LOGGER_URL, data, loggerPostOpts())
      .catch(err => { if (err.code !== 'ECONNABORTED') console.warn('Streaming log failed:', err.message); });
  }

  function logStats() {
    const statsData = getStats?.() || {};
    const data = {
      streamerId,
      projectId,
      os: osName,
      browserName,
      device: getDeviceTypeByUserAgent(),
      projectRegion: project.region,
      FPS: statsData['Framerate'] ?? null,
      latency: statsData['Net RTT (ms)'] ?? null,
      framesDecoded: statsData['Frames Decoded'] ?? null,
      framesDrop: statsData['Frames dropped'] ?? null,
      dataReceived: statsData['Received'] ?? null,
      packetLoss: statsData['Packets Lost'] ?? null,
      videoCodec: statsData['Video codec'] ?? null,
      audioCodec: statsData['Audio codec'] ?? null,
      videoResolution: statsData['Video resolution'] ?? null,
      videoBitrate: statsData['Video Bitrate (kbps)'] ?? null,
      audioBitrate: statsData['Audio Bitrate (kbps)'] ?? null,
      duration: statsData['Duration'] ?? null,
      controlsStreamInput: statsData['Controls stream input'] ?? null,
      videoQuantizationParameter: statsData['Video quantization parameter'] ?? null,
      location: geoData,
      time: new Date().toISOString(),
    };
    axios.post(LOGGER_URL, data, loggerPostOpts())
      .catch(err => { if (err.code !== 'ECONNABORTED') console.warn('Streaming log failed:', err.message); })
      .finally(() => {
        logStatsTimerRef.current = setTimeout(logStats, 60000);
      });
  }

  // Start (or restart) the 60-second stats reporter. A short probe waits up to
  // 10 s for the stats panel to show real numbers so the first row is useful,
  // then posts whatever is there. Called from _onWebRtcConnected, NOT from
  // onVideoInitialized: that callback only fires once the browser agrees to
  // play the video, so a viewer parked on the Start overlay (Edge "Block"
  // autoplay with no gesture, session_1788698314487_7083 on 2026-09-06) sent
  // no rows at all and was indistinguishable from a broken stream. Now the
  // rows arrive from the moment WebRTC connects and FPS reads 0 until play.
  function startLogStats() {
    stopLogStats();
    let attempts = 0;
    logStatsProbeRef.current = setInterval(() => {
      attempts++;
      const statsData = getStats();
      const netRtt = statsData['Net RTT (ms)'];
      const framerate = statsData['Framerate'];
      const ready = netRtt != null && framerate != null && !isNaN(netRtt) && !isNaN(framerate);
      if (ready || attempts >= 20) {
        clearInterval(logStatsProbeRef.current);
        logStatsProbeRef.current = null;
        logStats();
      }
    }, 500);
  }

  function stopLogStats() {
    clearInterval(logStatsProbeRef.current);
    logStatsProbeRef.current = null;
    clearTimeout(logStatsTimerRef.current);
    logStatsTimerRef.current = null;
  }

  function stopMediaHealth() {
    try { mediaHealthRef.current?.stop(); } catch (_) { /* ignore */ }
    mediaHealthRef.current = null;
  }

  // "Prefer TCP relay" is a per-tab preference set by the media-health retry:
  // it survives the reload that starts the fresh session, and any later
  // reload, so a viewer on a broken UDP path is not sent back down it.
  // ?transport=tcp in the URL sets it too, for support to hand out.
  const PREFER_TCP_KEY = 'sp:preferTcpRelay';
  const readPreferTcpRelay = () => {
    try {
      if (new URLSearchParams(window.location.search).get('transport') === 'tcp') return true;
      return sessionStorage.getItem(PREFER_TCP_KEY) === '1';
    } catch (_) { return false; }
  };
  const retryOverTcp = () => {
    try { sessionStorage.setItem(PREFER_TCP_KEY, '1'); } catch (_) { /* storage blocked in a third-party frame: the reload still happens */ }
    setShowMediaHealthToast(false);
    window.location.reload();
  };

  const startPlay = async () => {
    let resx;
    let resy;

    // Bind diagnostic helpers with closure over appStream / startTime
    const startTime = Date.now();
    const getDiag = (retry = 0) => getWebRtcDiagnostics(PixelStreamingUiApp, startTime, retry);
    const logDiag = (event, diag = {}) => logWebRtcDiagnostics(event,telemetryToken, diag, {
      streamerId,
      projectId,
      projectRegion: project.region,
      startTime
    });
    const preferTcpRelay = readPreferTcpRelay();
    stopMediaHealth();
    playRejectedRef.current = false;
    videoInitRef.current = false;


    let isVrEnabled = false;
    if (navigator.xr) {
      navigator.xr.isSessionSupported("immersive-vr").then(isSupported => {
        if (isSupported) {
          setXRSupport(true)
          isVrEnabled = true;
        };
      });
    }


    if (resolutionMode === "Fixed Resolution Mode") {
      resx = maxWidth;
      resy = maxHeight;
    }

    if (resolutionMode === "Dynamic Resolution Mode" || resolutionMode === "Crop on Resize Mode") {
      let viewportH = Math.floor(window.innerHeight * window.devicePixelRatio);
      let viewportW = Math.floor(window.innerWidth * window.devicePixelRatio);
      let maxPixel = Math.floor(maxHeight * maxWidth);
      let viewportPixel = viewportH * viewportW;

      if (maxPixel > viewportPixel) {
        resy = viewportH;
        resx = viewportW;
      } else {
        let divideNumber = Math.min(maxWidth / viewportW, maxHeight / viewportH);
        resx = Math.floor(divideNumber * viewportW);
        resy = Math.floor(divideNumber * viewportH);
      }
    }

    const { appStream, pixelStreaming, selectedCodec } = StreamPixelApplication({
      AutoPlayVideo: true,
      region: project.region,
      // Comes from the ticket response. There is no env-var fallback: the SDK
      // reads this only from settings, and the REACT_APP_*_SIGNALLING_SERVER
      // vars the comment used to cite no longer exist — the backend picks the
      // region's host so moving a region does not mean rebuilding every
      // embedding site's bundle.
      signallingHost: project.signallingHost,
      // Pinned test session: the backend's session id, so placement lands on the pinned host. Undefined otherwise.
      ...(pinnedSessionIdRef.current ? { sessionId: pinnedSessionIdRef.current } : {}),
      StartVideoMuted: true,
      AutoConnect: true,
      // NEVER hand the library a capture flag. With UseMic/UseCamera on,
      // Epic's receiveOffer() awaits getUserMedia() BEFORE createAnswer() — a
      // permission prompt the viewer ignores holds the SDP answer forever
      // (no video, no error, worker kills the session 10 min later; 14 of the
      // 16 genuine offer-never-answered sessions in the week to 2026-09-05
      // were showMic projects; the camera flag sits on the same await).
      // Mic and camera are attached after the stream is up instead — see
      // _onWebRtcSdpOffer and the post-connect block below.
      useMic: false,
      UseCamera: false,
      appId: projectId,
      unrealVersion:project?.unrealVersion || 5.2,
      primaryCodec,
      fallBackCodec: fallbaCodec,
      // Guard against a config that arrives without the field (older backend
      // or older project doc): a non-number silently disables AFK in the lib.
      afktimeout: Number(project.afktimeout) > 0 ? Number(project.afktimeout) : 600,
      touchInput: project.touchInput,
      mouseInput: project.mouseInput,
      gamepadInput: project.gamepadInput,
      resolution: project.resolution,
      hoverMouse: project.hoverMouse,
      xrInput: project.xrInput,
      streamerId,
      sfuHost,
      sfuPlayer,
      keyBoardInput: project.keyBoardInput,
      fakeMouseWithTouches: project.fakeMouseTouch,
      resX: resx,
      resY: resy,
      isVrEnabled,
      forceTurn:project.forceTurn || false,
      preferTcpRelay,
      sessionToken,
      // Dev mode: enables the lib's streamer re-poll (so the player attaches when
      // the editor comes up, and re-attaches after every restart) and turns AFK off.
      devMode: devModeRef.current,
    });

    PixelStreamingApp = pixelStreaming;
    PixelStreamingUiApp = appStream;

    let timeTaken = 0;

    

    PixelStreamingApp.addResponseEventListener('handle_responses', handleResponseApp);

    const webRtcControllerForCommand = appStream.stream._webRtcController;
    if (webRtcControllerForCommand) {
      webRtcControllerForCommand.onCommand = (rawMessage) => {
        try {
          const commandAsString = new TextDecoder('utf-16').decode(rawMessage.slice(1));
          const command = JSON.parse(commandAsString);
          if (command.showOnScreenKeyboard === true) {
            showEditTextModal(command.contents, webRtcControllerForCommand);
          }
        } catch (e) {
          console.warn('Failed to parse onCommand message:', e);
        }
      };

      // Send File (UE "Send File" / "Send File as Byte Array" blueprint nodes):
      // Epic's lib already receives the chunks and reassembles them into
      // webRtcController.file, but ships the browser-download step commented out.
      // We wrap the FromStreamer file handlers so a completed transfer is handed
      // to the browser as a download. Done here in app code (not a node_modules
      // patch) so it survives the build pipeline regardless of postinstall hooks.
      const smc = webRtcControllerForCommand.streamMessageController;
      if (smc?.fromStreamerHandlers) {
        const origFileExtension = smc.fromStreamerHandlers.get('FileExtension');
        const origFileContents = smc.fromStreamerHandlers.get('FileContents');
        // A new transfer always starts with the extension message; clear the
        // per-file download guard there so multiple files each download once.
        smc.fromStreamerHandlers.set('FileExtension', (data) => {
          origFileExtension?.(data);
          const f = webRtcControllerForCommand.file;
          if (f) { f.spDownloaded = false; f.spReceivedBytes = 0; f.spStamp = null; }
          spRemoveFileCard(); // a new transfer replaces any prior card
        });
        smc.fromStreamerHandlers.set('FileContents', (data) => {
          origFileContents?.(data); // let the lib append the chunk + mark valid
          const f = webRtcControllerForCommand.file;
          if (!f || !Array.isArray(f.data) || !f.chunks) return;
          const last = f.data[f.data.length - 1];
          f.spReceivedBytes = (f.spReceivedBytes || 0) + (last ? (last.byteLength || last.length || 0) : 0);
          if (f.data.length < f.chunks) {
            updateFileProgress(f.data.length, f.chunks, spFileName(f), f.spReceivedBytes);
          } else if (f.valid && !f.spDownloaded) {
            f.spDownloaded = true;
            try {
              const blob = new Blob([...f.data], { type: f.mimetype || 'application/octet-stream' });
              deliverReceivedFile(blob, spFileName(f), f.mimetype);
            } catch (e) {
              console.error('[streampixel] file download failed', e);
            }
          }
        });
      }
    }



    const showEditTextModal = (text, controller) => {
      if (editTextModalRef.current?.rootElement) {
        editTextModalRef.current.rootElement.remove();
      }
      const modal = new EditTextModal();
      editTextModalRef.current = modal;
      document.body.appendChild(modal.rootElement);
      Object.assign(modal.rootElement.style, { top: '45%' });
      modal.showModal(text);
      modal.events.addEventListener('editConfirmed', (e) => {
        controller.streamMessageController
          .toStreamerHandlers.get('TextboxEntry')?.([e.detail]);
      });
    };

    pixelStreaming._onWebRtcSdpOffer = async () => {
      // This fires after setRemoteDescription(offer) and BEFORE createAnswer(),
      // so the offer's audio m-line already exists on the peer connection.
      // Mark it sendrecv now (synchronously — before the first await) so the
      // answer tells UE "I may send audio" while sending nothing; the track is
      // attached post-connect with replaceTrack, which needs no renegotiation.
      // Nothing here can wait on a permission prompt.
      if (project.showMic || project.showCamera) {
        try {
          const pc = appStream.stream._webRtcController?.peerConnectionController?.peerConnection;
          for (const t of pc?.getTransceivers() ?? []) {
            const kind = t.receiver?.track?.kind;
            const wanted = (kind === 'audio' && project.showMic) || (kind === 'video' && project.showCamera);
            if (wanted && t.direction !== 'sendrecv') t.direction = 'sendrecv';
          }
        } catch (e) {
          console.warn('capture: could not mark the media line sendrecv', e);
        }
      }
      message = { type: "stream-state", value: "Starting connection to Streampixel server, please wait" };
      window.parent.postMessage(message, "*");
      clearTimeout(hostWaitTimer);
      setAuthMessage(loadingText('one'));
      logStatsStart("connecting");
      const diagnostics = await getDiag();
      logDiag("sdpOffer", diagnostics);
    };

    pixelStreaming._onWebRtcSdpAnswer = async () => {
      message = { type: "stream-state", value: "Almost there, hold tight- awesomeness loading" };
      window.parent.postMessage(message, "*");
      setAuthMessage(loadingText('two'));
      logStatsStart("Almost there, hold tight- awesomeness loading");
      const diagnostics = await getDiag();
      logDiag("sdpAnswer", diagnostics);

      setTimeout(async () => {
        const d = await getDiag();
        if (d.iceConnectionState !== 'connected' &&
          d.iceConnectionState !== 'completed' &&
          d.connectionState !== 'connected') {
          // Classify WHY the connection never came up, from the same
          // diagnostics we log. Order matters (validated against 20 days of
          // production timeouts): a client that gathered nothing often ALSO
          // sees zero remotes — its broken network must win over "streamer
          // mute". Anything ambiguous falls through to the generic message.
          const remotes = Array.isArray(d.remoteCandidates) ? d.remoteCandidates.length : d.remoteCandidates;
          const bytes = d.transport ? d.transport.bytesReceived : null;
          let failureClass = 'unclassified';
          let failureMessage = null;
          if (d.turnCandidatesFound === false && d.srflxCandidatesFound === false) {
            failureClass = 'nothing-gathered';
            failureMessage = 'Your network is blocking the connection entirely. Please try a different network.';
          } else if (remotes === 0) {
            failureClass = 'streamer-mute';
            failureMessage = 'The stream did not respond. Please refresh the page to try again.';
          } else if ((d.candidatePairs || 0) > 0 && (d.connectedPairs || 0) === 0 && bytes === 0 && d.turnCandidatesFound === true) {
            failureClass = 'network-blackhole';
            failureMessage = 'Your network appears to block video streaming traffic. Try a different network — a mobile hotspot usually works — or ask IT to allow WebRTC.';
          } else if (bytes > 0) {
            failureClass = 'progressed-then-stalled';
          }
          logDiag("timeout", { timeSinceStart: Date.now() - startTime, failureClass, ...d });
          // SHOW the explanation at 60s, not 30s. 20 days of history: of 437
          // timeouts, 37 (8.5%) still connected within the next 30-60s (slow
          // ICE / the auto-reconnect landing) — at 60s the premature-message
          // rate drops to ~3% while the 31% who will only ever recover by
          // refreshing (median 6.5 MINUTES later without guidance) get told
          // what to do. Logging stays at 30s so telemetry stays comparable
          // with history. The message never kills the attempt — if the
          // connection lands afterwards, the stream simply takes over.
          if (failureMessage) {
            setTimeout(async () => {
              const d2 = await getDiag();
              if (d2.iceConnectionState !== 'connected' &&
                d2.iceConnectionState !== 'completed' &&
                d2.connectionState !== 'connected') {
                setAuthMessage(failureMessage);
              }
            }, 30000);
          }
        }
      }, 30000);
    };

    pixelStreaming._onWebRtcConnected = async () => {
      setAuthMessage(loadingText('three'));
      message = { type: "stream-state", value: "finalising" };
      window.parent.postMessage(message, "*");
      logStatsStart("Sharpening pixels and buffing the details...");
      const diagnostics = await getDiag();

      const tcpAtConnect = await isTcpRelayActive(appStream);
      // Selected-path attribution (host/srflx/relay + which TURN provider) —
      // the per-session record the TURN cost work reads. See getSelectedPathInfo.
      const pathInfo = await getSelectedPathInfo(appStream);
      logDiag("connected", { ...diagnostics, tcpFallback: tcpAtConnect, pathInfo, preferTcpRelay });
      startLogStats();

      // Media health: is video actually reaching this viewer? Direct sessions
      // only — meeting viewers have the SFU recovery loop below. Measurement
      // first: every verdict is logged; the only action is a toast offering a
      // TCP-relay retry on a lossy path, and only when this session is not
      // already the TCP retry.
      if (!sfuPlayerRef.current) {
        stopMediaHealth();
        const monitor = createMediaHealthMonitor({
          getVideoElement: () => appStream.stream?.videoElementParent?.querySelector('video'),
          getPeerConnection: () => findPcForTcpCheck(appStream),
          isHidden: () => document.hidden,
          getFlags: () => ({ playRejected: playRejectedRef.current, videoInitialized: videoInitRef.current }),
          log: (m) => console.warn(m),
          onVerdict: async (cls, snapshot) => {
            const path = await getSelectedPathInfo(appStream);
            logDiag('mediaHealth', { ...snapshot, pathInfo: path, preferTcpRelay, visibility: document.visibilityState });
            if (cls === MEDIA_CLASS.LOSSY_PATH && !preferTcpRelay) setShowMediaHealthToast(true);
          },
          onRecovered: (cls, snapshot) => {
            logDiag('mediaHealthRecovered', { ...snapshot, previousClass: cls, preferTcpRelay });
            setShowMediaHealthToast(false);
          },
        });
        mediaHealthRef.current = monitor;
        monitor.start();

        // The TCP retry's report card: 12 s after connecting, did frames arrive?
        if (preferTcpRelay) {
          setTimeout(() => {
            const s = monitor.getLastSnapshot();
            logDiag('mediaHealthRetryOutcome', { ...(s || {}), pathInfo, recovered: !!(s && (s.everHadFrames || s.class === MEDIA_CLASS.HEALTHY)) });
          }, 12000);
        }
      }
      if (tcpAtConnect) {
        setShowTcpFallbackToast(true);
        logDiag("tcpFallbackDetected", { when: "connected" });
      } else {
        setTimeout(async () => {
          if (await isTcpRelayActive(appStream)) {
            setShowTcpFallbackToast(true);
            logDiag("tcpFallbackDetected", { when: "+5s" });
          }
        }, 5000);
      }
    };

    appStream.stream._onVideoEncoderAvgQP = (qp) => setAvgQp(normalizeQpToH264Scale(qp, selectedCodec));

    try {
      const webRtcCtrl = appStream.stream._webRtcController;
      if (webRtcCtrl?.protocol) {
        import('@epicgames-ps/lib-pixelstreamingcommon-ue5.7').then(({ Messages }) => {
          webRtcCtrl.protocol.addListener(Messages.config.typeName, (configMsg) => {
            const opts = configMsg.peerConnectionOptions;
            const iceServers = opts?.iceServers || [];
            if (!iceServers.length) {
              console.error('⚠️ CRITICAL: Config message has EMPTY/MISSING iceServers!', { opts });
              logDiag("configMessageMissingIceServers", {
                error: "Config message missing ICE servers",
                peerConnectionOptions: opts, iceServers,
              });
            } else {
              logDiag("configMessageReceived", {
                iceServersCount: iceServers.length,
                iceServers: iceServers.map(s => ({ urls: s.urls, hasCreds: !!(s.username || s.credential) })),
              });
            }
          });
        }).catch(e => console.warn('Failed to load pixelstreamingcommon:', e));
      }
    } catch (e) {
      console.warn('Failed to intercept config message:', e);
    }

    try {
      const wsCtrl = appStream.stream.signallingProtocol;
      if (wsCtrl?.transport) {
        if (wsCtrl.isConnected()) {
          setIsMuted(true);
          setRoomConnect(false);
          setToogleMicChat(false);
          logDiag("signalingServerConnected", {
            initialState: true,
            timestamp: new Date().toISOString(),
            timeSinceStart: Date.now() - startTime,
          });
        }
        wsCtrl.transport.on('open', () => {
          logDiag("signalingServerConnected", {
            timestamp: new Date().toISOString(),
            timeSinceStart: Date.now() - startTime,
          });
        });
        wsCtrl.transport.on('error', (error) => {
          console.error('❌ Signaling server error:', error);
          // The browser's WebSocket error event carries no detail by spec, so
          // `error` is usually undefined — log the context that explains the
          // error instead: offline blip vs hidden/suspended tab vs socket state.
          // The paired close event (below) carries the code/reason.
          logDiag("signalingServerError", {
            error: error?.message || String(error),
            wsReadyState: wsCtrl.transport?.webSocket?.readyState,
            online: navigator.onLine,
            visibility: document.visibilityState,
            timeSinceStart: Date.now() - startTime,
          });
        });
      }
    } catch (e) {
      console.warn('Failed to log signaling server events:', e);
    }

    try {
      const webRtcCtrl2 = appStream.stream._webRtcController;
      if (webRtcCtrl2?.peerConnection) {
        const pc = webRtcCtrl2.peerConnectionController?.peerConnection;
        pc.addEventListener('iceconnectionstatechange', async () => {
          if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'disconnected') {
            const diagnostics = await getDiag();
            logDiag("iceFailure", {
              failureState: pc.iceConnectionState,
              timeSinceStart: Date.now() - startTime,
              ...diagnostics,
            });
          }
        });
        pc.addEventListener('connectionstatechange', async () => {
          if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
            const diagnostics = await getDiag();
            logDiag("connectionFailure", {
              failureState: pc.connectionState,
              timeSinceStart: Date.now() - startTime,
              ...diagnostics,
            });
          }
        });
      }
    } catch (_) { /* don't break connection if logging fails */ }

    const wsController = appStream.stream.signallingProtocol;
    const isConnected = wsController.isConnected();

    if (isConnected) {
      wsController.transport.on('message', async (msgRaw) => {
        try {
          if (msgRaw.message === "locationData") {
            geoData = {
              country: msgRaw.data.country,
              countryCode: msgRaw.data.country_code,
              state: msgRaw.data.state,
              city: msgRaw.data.city,
              latitude: msgRaw.data.latitude,
              longitude: msgRaw.data.longitude,
              zip: msgRaw.data.zip,
            };
            const diagnostics = await getDiag();
            logDiag("initialConnection", diagnostics);
            logStatsStart("Starting connection to Streampixel server, please wait");
          }

          if (msgRaw.message === "You are in Queue") {
            const msgFormat = `${msgRaw.message} ${msgRaw.position}`;
            setAuthMessage(msgFormat);
            setLoading(true);
            window.parent.postMessage({ type: "stream-state", value: `queue-${msgRaw.position}` }, "*");
            logStatsStart(msgRaw.message);
          }
        } catch (error) {
          console.error("Failed to parse message:", error);
        }
      });

      // AFK: the SDK shows the countdown overlay, but when the countdown
      // EXPIRES the lib just tears the connection down — the viewer then saw a
      // bare "Disconnected code:NNNN." with no hint they idled out, and the
      // close handler treated the deliberate idle-kick like a network blip and
      // tried to reconnect. Catch the lib's own expiry event so the goodbye
      // says why and no pointless reconnect is attempted.
      let afkTimedOut = false;
      appStream.stream.addEventListener('afkTimedOut', () => {
        afkTimedOut = true;
      });

      // Playback telemetry. The lib refuses to fire onVideoInitialized until the
      // browser accepts play(); when the browser refuses (Edge "Block" autoplay,
      // no user gesture because the password came from the URL) it raises
      // playStreamRejected and shows the Start overlay instead. Nothing recorded
      // either outcome, so a viewer who never pressed Start looked like a broken
      // stream. Two rows make the timeline explicit: rejected (with the DOM
      // error name) and initialized (playback really began).
      appStream.stream.addEventListener('playStreamRejected', ({ data } = {}) => {
        const reason = data?.reason;
        playRejectedRef.current = true;
        logDiag('playStreamRejected', {
          reasonName: reason?.name ?? String(reason ?? 'unknown'),
          reasonMessage: reason?.message ?? null,
          visibility: document.visibilityState,
        });
      });
      appStream.stream.addEventListener('videoInitialized', () => {
        videoInitRef.current = true;
        logDiag('videoInitialized', { visibility: document.visibilityState });
      });

      wsController.transport.on('close', async (data) => {
        logDiag("signalingServerDisconnected", {
          code: data.code, reason: data.reason,
          timeSinceStart: Date.now() - startTime,
        });

        setIsMuted(true);
        stopMediaHealth();
        if (!sfuPlayerRef.current) {
          setRoomConnect(false);
          setToogleMicChat(false);
          setRoomDisconnect(true);
        }
        stopLogStats();

        let disconnectMessageText = `Disconnected code:${data.code}.`;

        const closeReasons = [
          "Project Inactive", "Worker Node Unavailable", "Application Not Found",
          "Application Error", "Maximum Runtime Reached", "No streamer connected",
        ];

        // 1008 is the signalling gate REFUSING admission (ticket invalid or
        // expired, or a malformed URL) — retrying presents the same refused
        // credentials forever. An AFK expiry is a deliberate kick. Neither is
        // a network blip; neither reconnects.
        const isReconnectable =
          !afkTimedOut && data.code !== 1008 &&
          (data.code === 1005 || data.code === 1006 ||
          // Dev mode: a dev streamer restart closes the viewer with 4006. That is
          // the ITERATION loop, not a terminal error — reconnect and re-attach
          // when the editor comes back up.
          (devModeRef.current && data.code === 4006) ||
          (sfuPlayerRef.current && !closeReasons.includes(data.reason)));

        if (isReconnectable) {
          const now = Date.now();

          setLogoStyles(logoOffsets(project.logoPosition));

          if (!reconnectWindowStart) {
            reconnectWindowStart = now;
          }

          const elapsed = now - reconnectWindowStart;

          if (elapsed > 60000 && !sfuPlayerRef.current) {
            reconnectWindowStart = null;
            reconnecting = false;
            setAuthMessage('Disconnected');
            setReconnect(false);
            setIsStreamConnect(false);
            setShowTcpFallbackToast(false);
            setLoading(false);
            setShowUiBtn(false);
            window.parent.postMessage({ type: "stream-state", value: "disconnected", code: 4007 }, "*");
            setDisconnectMessage('Disconnected code:4007. We could not reconnect to the stream.');
            logDiag("signalingServerDisconnected", {
              code: 4007, reason: "Reconnection Failed",
              timeSinceStart: Date.now() - startTime,
            });
            return;
          }

          if (reconnecting) return;

          reconnecting = true;

          window.parent.postMessage({ type: "stream-state", value: "reconnecting" }, "*");
          logStatsStart("reconnecting");
          setAuthMessage(sfuPlayerRef.current
            ? "Waiting for the host to reconnect…"
            : 'Reconnecting…');
          setReconnect(true);
          setIsStreamConnect(true);
          setLoading(true);
          setShowUiBtn(false);

          const webRtcPlayer = appStream.stream._webRtcController;
          setTimeout(() => {
            webRtcPlayer.connectToSignallingServer();
            reconnecting = false;
          }, 5000);
        }else{

        // Viewer-facing wording for the platform's close codes. 4006 (the most
        // common mid-session failure) previously fell through every branch and
        // showed a bare "Disconnected code:4006." with no explanation or next
        // step. Wording is deliberately neutral: 4006 covers both a crash and
        // an intentional in-app close — the code cannot distinguish them, so
        // neither does the message.
        const friendlyCloseMessages = {
          // 4000 now means ONLY "Project Inactive" — the owner turned the stream off.
          // (Session teardowns moved to 4009 so the two are no longer conflated.)
          4000: 'This stream has been turned off by the project owner.',
          4001: 'No streaming server is available right now. Please try again in a few minutes.',
          4002: 'The application could not be found on the streaming server. Please contact the project owner.',
          4003: 'The application reported an error and could not continue. Refresh to try again.',
          4004: 'The session ended after reaching its maximum allowed runtime.',
          4005: 'The application took too long to start. Refresh the page to try again.',
          4006: 'The application closed unexpectedly. Refresh the page to start a new session.',
          // 4009 = the session ended / was torn down (e.g. a reconnect after the
          // session was already cleaned up). Distinct from 4000 project-off.
          4009: 'The session has ended. Refresh the page to start a new one.',
        };

        if (afkTimedOut) {
          disconnectMessageText = 'Disconnected due to inactivity. The session closed after no input for a while — refresh to start again.';
        } else if (data.code === 1008) {
          disconnectMessageText = data.reason === 'Unauthorized'
            ? 'Disconnected code:1008. Access was refused — the stream ticket is invalid or has expired. Refresh the page to request a new session.'
            : `Disconnected code:1008. ${data.reason || 'The stream URL is missing required parameters.'}`;
        } else if (friendlyCloseMessages[data.code]) {
          disconnectMessageText = `${friendlyCloseMessages[data.code]} (code ${data.code})`;
        } else if (closeReasons.includes(data.reason)) {
          disconnectMessageText = `Disconnected code:${data.code}. ${data.reason}`;
        }
        setDisconnectMessage(disconnectMessageText);

        window.parent.postMessage({
          type: "stream-state", value: "disconnected",
          disconnectCode: data.code, reason: afkTimedOut ? 'inactivity' : data.reason,
        }, "*");

        logStatsStart("Disconnected", { disconnectCode: data.code, reason: data.reason });

        }
      });
    }

    videoRef.current.append(appStream.rootElement);

    const videoElement = appStream?.stream?.videoElementParent?.querySelector("video");
    if (videoElement) {
      videoElement.muted = false;
      videoElement.focus();
      videoElement.autoplay = true;
      videoElement.tabIndex = 0;
    }

    hideLoadingSpinner();

    appStream.onDisconnect = function () {
      setIsMuted(true);
      setRoomConnect(false);
      setToogleMicChat(false);
      setRoomDisconnect(true);
      setSfuChatActive(false);

      if (sfuRecoveryLoop) { clearInterval(sfuRecoveryLoop); sfuRecoveryLoop = null; }
      if (psNegotiationListener) {
        try { window.removeEventListener('streampixel:negotiation-failed', psNegotiationListener); } catch (_) {}
        psNegotiationListener = null;
      }
      clearTimeout(hostWaitTimer);
      hostWaitTimer = null;

      message = { type: "stream-state", value: "disconnected" };

      document.querySelector('.loading-spinner')?.remove();

      window.parent.postMessage(message, "*");
      setIsStreamConnect(false);
      setShowTcpFallbackToast(false);
      setShowMediaHealthToast(false);
      setAuthMessage('Stream ended');
      stopLogStats();
      stopMediaHealth();
    };

    appStream.onVideoInitialized = function () {
      applyCodecQpOverride(appStream.stream, selectedCodec, primaryCodec);

      if (sfuPlayerRef.current && !psNegotiationListener) {
        const NEG_RECOVER_COOLDOWN_MS = 4000;
        psNegotiationListener = (ev) => {
          const now = Date.now();
          if (now - lastNegotiationRecoverAt < NEG_RECOVER_COOLDOWN_MS) return;
          lastNegotiationRecoverAt = now;
          const state = ev && ev.detail && ev.detail.state;
          console.warn('[ps-neg] negotiation failed (pcState=' + state + ') — re-driving signalling');
          try { setAuthMessage('Restoring stream…'); } catch (_) {}
          try { setNegotiationStatus('WebRTC renegotiating…'); } catch (_) {}
          try {
            const ws = appStream?.stream?.signallingProtocol?.ws;
            if (ws && ws.readyState === WebSocket.OPEN) ws.close();
          } catch (_) {}
          setTimeout(() => {
            try { setNegotiationStatus((cur) => (cur === 'WebRTC renegotiating…' ? '' : cur)); } catch (_) {}
          }, 5000);
        };
        window.addEventListener('streampixel:negotiation-failed', psNegotiationListener);
      }

      if (sfuPlayerRef.current && !sfuRecoveryLoop) {
        let unhealthySince = null;
        let messageShown = false;
        let lastFrameAt = Date.now();
        let lastFrameCount = 0;
        let trackedEl = null;
        const MESSAGE_MS = 1000;
        const FRAME_STALE_MS = 3500;
        const GIVE_UP_MS = 10 * 60000;

        // A stream that has not started yet is NOT a stream that has broken.
        // There is no <video> element until the first offer is answered, and
        // `healthy` requires one — so every tick before that counted as
        // unhealthy. Tick 1 set unhealthySince, tick 2 passed MESSAGE_MS, and
        // the page reloaded ~2s after connecting, every time: viewers appeared
        // in signalling as a fresh PlayerN every ~3.5s and never saw video.
        // Startup is held off until the stream has been healthy at least once;
        // after that a stall is real and the reload below is right.
        //
        // The grace window still expires, so a stream that genuinely never
        // starts recovers instead of hanging — just on a 45s cadence rather
        // than a 2s one.
        const STARTUP_GRACE_MS = 45000;
        const loopStartedAt = Date.now();
        let everHealthy = false;

        // Reloading is the recovery, but it clears every JS variable that could
        // remember it happened — which is what let the loop run unbounded.
        // sessionStorage survives the reload and is per-tab, so it is what makes
        // "stop after N attempts" possible at all. Wrapped because embedding
        // sites can block storage in a third-party frame.
        const RELOADS_KEY = 'sp:sfuRecoveryReloads';
        const MAX_RECOVERY_RELOADS = 3;
        const reloadCount = () => {
          try { return parseInt(sessionStorage.getItem(RELOADS_KEY), 10) || 0; } catch (_) { return 0; }
        };
        const noteReload = () => {
          try { sessionStorage.setItem(RELOADS_KEY, String(reloadCount() + 1)); } catch (_) {}
        };
        const clearReloads = () => {
          try { sessionStorage.removeItem(RELOADS_KEY); } catch (_) {}
        };

        const attachFrameTracker = (ve) => {
          if (!ve || ve === trackedEl) return;
          trackedEl = ve;
          lastFrameAt = Date.now();
          lastFrameCount = 0;
          if (typeof ve.requestVideoFrameCallback === 'function') {
            const onFrame = () => {
              if (trackedEl !== ve) return;
              lastFrameAt = Date.now();
              try { ve.requestVideoFrameCallback(onFrame); } catch (_) {}
            };
            try { ve.requestVideoFrameCallback(onFrame); } catch (_) {}
          }
        };

        sfuRecoveryLoop = setInterval(() => {
          if (document.hidden) { unhealthySince = null; lastFrameAt = Date.now(); return; }

          const ve = appStream.stream?.videoElementParent?.querySelector('video');
          attachFrameTracker(ve);
          const vtrack = (ve && ve.srcObject && typeof ve.srcObject.getVideoTracks === 'function')
            ? ve.srcObject.getVideoTracks()[0]
            : null;

          if (ve && ve.readyState >= 2 && ve.paused) {
            ve.play().catch(() => {});
          }

          let frameCount = null;
          try {
            if (ve && typeof ve.getVideoPlaybackQuality === 'function') {
              frameCount = ve.getVideoPlaybackQuality().totalVideoFrames;
            } else if (ve && typeof ve.webkitDecodedFrameCount === 'number') {
              frameCount = ve.webkitDecodedFrameCount;
            }
          } catch (_) {}
          if (frameCount != null && frameCount > lastFrameCount) {
            lastFrameCount = frameCount;
            lastFrameAt = Date.now();
          }
          const haveFrameSignal = frameCount != null
            || (!!ve && typeof ve.requestVideoFrameCallback === 'function');
          const framesFresh = haveFrameSignal
            ? (Date.now() - lastFrameAt < FRAME_STALE_MS)
            : (!!vtrack && vtrack.muted === false);
          const trackDead = !vtrack || vtrack.readyState === 'ended';
          const healthy = !!ve && ve.readyState >= 2 && !ve.paused && !trackDead && framesFresh;

          if (healthy) {
            unhealthySince = null;
            // Video is flowing, so whatever prompted an earlier reload is over.
            // Clearing here (rather than on connect) is deliberate: a reload
            // that reconnects but still shows nothing must keep its count.
            if (!everHealthy) { everHealthy = true; clearReloads(); }
            if (messageShown) {
              messageShown = false;
              setReconnect(false);
              setLoading(false);
              setShowUiBtn(true);
              setAuthMessage('');
              window.parent.postMessage({ type: 'stream-state', value: 'loadingComplete' }, '*');
            }
            return;
          }

          // Still starting up: no video element yet, or one that has not decoded
          // its first frame. Not a fault, and reloading here only restarts the
          // same wait.
          if (!everHealthy && Date.now() - loopStartedAt < STARTUP_GRACE_MS) {
            unhealthySince = null;
            return;
          }

          if (unhealthySince === null) { unhealthySince = Date.now(); return; }
          const unhealthyFor = Date.now() - unhealthySince;

          if (unhealthyFor >= GIVE_UP_MS) {
            console.warn('[sfuRecovery] host did not return after ' + GIVE_UP_MS + 'ms — giving up');
            logDiag('sfuRecoveryGaveUp', { timeSinceStart: Date.now() - startTime });
            clearInterval(sfuRecoveryLoop);
            sfuRecoveryLoop = null;
            setLoading(false);
            setShowUiBtn(false);
            setSfuChatActive(false);
            setDisconnectMessage("Host hasn't returned — tap to reconnect");
            setIsStreamConnect(false);
            window.parent.postMessage({ type: 'stream-state', value: 'disconnected' }, '*');
            return;
          }

          if (!messageShown && unhealthyFor >= MESSAGE_MS) {
            messageShown = true;

            // Reloading has not helped MAX_RECOVERY_RELOADS times running, so
            // it is not going to. Hand the viewer a control instead of putting
            // the tab into another reload it cannot interrupt.
            if (reloadCount() >= MAX_RECOVERY_RELOADS) {
              console.warn('[sfuRecovery] still no video after ' + reloadCount() + ' reloads — stopping');
              logDiag('sfuRecoveryGaveUp', {
                reason: 'reload limit', reloads: reloadCount(),
                timeSinceStart: Date.now() - startTime,
              });
              clearReloads();
              clearInterval(sfuRecoveryLoop);
              sfuRecoveryLoop = null;
              setLoading(false);
              setShowUiBtn(false);
              setSfuChatActive(false);
              setDisconnectMessage("Stream didn't come back — tap to reconnect");
              setIsStreamConnect(false);
              window.parent.postMessage({ type: 'stream-state', value: 'disconnected' }, '*');
              return;
            }

            setAuthMessage('Host disconnected — reconnecting…');
            setReconnect(true);
            setLoading(true);
            setShowUiBtn(false);
            // Both of these must happen BEFORE the reload — it tears down the
            // page, so anything after it never runs. The embedding site was
            // never getting this state change.
            window.parent.postMessage({ type: 'stream-state', value: 'reconnecting' }, '*');
            noteReload();
            window.location.reload(true);
          }
        }, 1000);
      }

      setIsMuted(true);
      setRoomConnect(false);
      setToogleMicChat(false);


      const xrBtn = document.getElementById('xrBtn');
      if (xrBtn) {
        xrBtn.style.display = "none";
      }
      setLogoStyles(logoOffsets(branding.logoPosition));

      const statsBtn = document.getElementById('statsBtn');
      setReconnect(false);
      reconnectWindowStart = null;
      reconnecting = false;

      if (statsBtn) {
        statsBtn.style.display = 'none';
        statsBtn.disabled = true;
      }
      const panel = document.getElementById('stats-panel');
      if (panel) panel.style.display = 'none';

      const endTime = Date.now();
      timeTaken = (endTime - startTime) / 1000;

      message = { type: "stream-state", value: "loadingComplete" };
      setLoading(false);
      window.parent.postMessage(message, "*");

      if (resolutionMode === "Fixed Resolution Mode" && videoElement) {
        videoElement.style.objectFit = 'contain';
      }

      if (resolutionMode === "Crop on Resize Mode") {
        const vpele = document.getElementById("streamingVideo");
        if (vpele) vpele.removeAttribute('style');
      }

      const liveWsController = appStream.stream.signallingProtocol;

      window.addEventListener("offline", () => {
        if (liveWsController.isConnected()) {
          if (liveWsController.ws?.readyState === WebSocket.OPEN) liveWsController.ws.close();
          liveWsController.disconnect();
        }
      });

      liveWsController.transport.on('close', () => {
        if (!sfuPlayerRef.current) setRoomDisconnect(true);
      });

      setShowUiBtn(true);
      if (sfuPlayerRef.current) setSfuChatActive(true);

      document.querySelector('.loading-spinner')?.remove();

      setTimeout(() => {
        handleResolution(`${resx}x${resy}f`);
        hideLoadingSpinner();

        if (startChatIf !== "Dynamic" && isChatEnabled(project)) {
          setShowChatUI(true);
          setRoomConnect(true);
          setRoomDisconnect(false);
        }

        setIsHovering(project.hoverMouse);

        // {} is truthy, so a bare `if (ueMessage)` sent an empty descriptor to
        // Unreal on every session with no query params — a guaranteed
        // "no event type field" error in any strict UE app.
        if (ueMessage && Object.keys(ueMessage).length > 0) {
          handleSendCommand(ueMessage);
        }
        ueMessage = null;
      }, 700);

      // Microphone / webcam AFTER the stream is up, never before. Their media
      // lines were negotiated sendrecv in _onWebRtcSdpOffer, so replaceTrack()
      // lights them up with no renegotiation. Denied or ignored prompt = stream
      // unaffected, capture simply off. Mic constraints match Epic's library.
      // Do NOT call pixelStreaming.unmuteMicrophone(true) / unmuteCamera(true)
      // here: with the flag off each forces a full reconnect. The diag row
      // carries the negotiated direction — 'recvonly' there means UE's offer
      // did not accept that media back and the track is attached but unsent.
      const attachCapture = async (kind, constraints, event) => {
        const pc = appStream.stream._webRtcController?.peerConnectionController?.peerConnection;
        const transceiver = pc?.getTransceivers().find((t) => t.receiver?.track?.kind === kind);
        if (!transceiver) {
          logDiag(event, { ok: false, reason: `no-${kind}-transceiver` });
          return;
        }
        try {
          const stream = await navigator.mediaDevices.getUserMedia(constraints);
          const track = stream.getTracks().find((t) => t.kind === kind);
          if (!track || pc.connectionState === 'closed') { stream.getTracks().forEach((t) => t.stop()); return; }
          await transceiver.sender.replaceTrack(track);
          logDiag(event, { ok: true, direction: transceiver.currentDirection || transceiver.direction });
        } catch (err) {
          console.error(`${kind} capture blocked or failed:`, err);
          logDiag(event, { ok: false, reason: err?.name || String(err) });
        }
      };
      if (project.showMic) {
        attachCapture('audio', {
          video: false,
          audio: { autoGainControl: false, channelCount: 1, echoCancellation: false, latency: 0,
                   noiseSuppression: false, sampleRate: 48000, sampleSize: 16, volume: 1.0 },
        }, 'micAttach');
      } else if (project.voiceChat) {
        // Pre-warm the permission for the LiveKit room (unchanged behaviour).
        navigator.mediaDevices.getUserMedia({ audio: true })
          .then((stream) => stream.getTracks().forEach((t) => t.stop()))
          .catch((err) => console.error('Microphone access blocked or failed:', err));
      }
      if (project.showCamera) {
        attachCapture('video', { video: true, audio: false }, 'camAttach');
      }
      // The stats reporter used to start here; it now starts in
      // _onWebRtcConnected (see startLogStats) so unplayed streams still report.
    };
  };

  function toggleMouseControl() {
    setIsHovering(prev => {
      const next = !prev;
      PixelStreamingUiApp?.stream._webRtcController.config
        .setFlagEnabled(Flags.HoveringMouseMode, next);
      return next;
    });
  }

  // Join (or re-join) the chat room from a comms message. Single source of
  // truth for both the UE data-channel and parent-iframe paths.
  // - Cancels any in-flight disconnect teardown so it can't clobber this join.
  // - Guards on roomConnectRef (live value) to avoid a double-connect against
  //   the session-begin auto-connect.
  const connectChatRoom = (value) => {
    if (!value || !isChatEnabled(project) || roomConnectRef.current) return;
    if (disconnectTimerRef.current) {
      clearTimeout(disconnectTimerRef.current);
      disconnectTimerRef.current = null;
    }
    roomName = `${projectId}#${value.roomId}`;
    userName = value.name;
    avatar = value.pfpUrl;
    setShowChatUI(true);
    setRoomConnect(true);
    setRoomDisconnect(false);
  };

  // Leave the chat room. The actual LiveKit leave fires immediately via the
  // roomDisconnect effect; the 3s timer just defers hiding the UI / clearing
  // roomConnect for a graceful teardown. Re-entrancy-safe: replaces any prior
  // pending timer, and a connectChatRoom() cancels it.
  const disconnectChatRoom = () => {
    if (!roomConnectRef.current) return;
    setRoomDisconnect(true);
    if (disconnectTimerRef.current) clearTimeout(disconnectTimerRef.current);
    disconnectTimerRef.current = setTimeout(() => {
      setShowChatUI(false);
      setRoomConnect(false);
      disconnectTimerRef.current = null;
    }, 3000);
  };

  const handleResponseApp = (response) => {
    try {
      const newRes = JSON.parse(response);

      if (newRes.message?.type === 'comms') {
        if (newRes.message.value === "disconnect") {
          disconnectChatRoom();
        } else {
          connectChatRoom(newRes.message.value);
        }
      }

      if (newRes.message?.type === 'heartbeat') {
        document.getElementById("afkOverlayInner")?.click();
      }

      if (newRes.message === 'requestScreenshot') handleScreenshot();

      if (newRes.message?.type === 'togglehoveringmouse') {
        PixelStreamingUiApp?.stream._webRtcController.config
          .setFlagEnabled(Flags.HoveringMouseMode, newRes.message.value);
      }
    } catch (_) {
      if (response.includes("requestScreenshot")) handleScreenshot();

      if (response.includes('https')) {
        try {
          const url = new URL(response.trim());
          if (url.protocol === 'https:') {
            window.open(url.href, '_blank', 'noopener,noreferrer');
          }
        } catch (_) { /* not a valid URL — ignore */ }
      }
    }

    window.parent.postMessage(response, '*');
  };

  useEffect(() => {
    // Origin of the page that embeds this player (null when we're top-level, or
    // when the browser won't expose the ancestor). Captured once per mount.
    const embedderOrigin = (() => {
      try {
        const raw = getParentFrameUrl();
        return raw ? new URL(raw).origin : null;
      } catch (_) {
        return null;
      }
    })();

    const handleMessage = (event) => {
      // When the player is top-level, window.parent === window, so our own
      // postMessage calls self-deliver here — and pass the source check
      // below, since the window IS its own parent — then get forwarded back
      // to Unreal: an infinite reflection loop. With no embedder there is
      // nothing to bridge, so ignore all window messages.
      if (window.parent === window) return;

      // The player is embeddable on any customer site, so there is no fixed
      // origin to allowlist. Instead we honor control messages only from the
      // DIRECT embedding window, and — when the browser exposes it — only from
      // that window's origin. A hostile second frame, popup, or unrelated tab
      // (which is how the postMessage-injection attack works) is ignored.
      if (event.source !== window.parent) return;
      if (embedderOrigin && event.origin !== embedderOrigin) return;
      if (event.data == null) return;

      const { message } = event.data;

      // passwordSubmit is deliberately NOT accepted over postMessage: honoring it
      // would let an embedding page script password guesses against the stream.
      // The password is entered in the player's own UI (handlePasswordSubmit),
      // and the ticket endpoint is rate-limited server-side.

      if (PixelStreamingUiApp) {
        if (message === "heartbeat") document.getElementById("afkOverlayInner")?.click();

        if (message === "requestScreenshot") handleScreenshot();

        if (message?.type === "setResolution") {
          if (resolutionSetting.includes(message.value)) {
            const match = message.value.match(/\((\d+x\d+)\)/);
            if (match) {
              handleResMax(match[1], message.value);
              activeResolution = message.value;
            }
          }
        }

        if (message === "terminateSession") handleTerminateSession();

        // Both elements — see setStreamAudioMuted for why the <audio>
        // element alone is not enough in SFU sessions.
        if (message === "muteAudio") {
          setStreamAudioMuted(true);
          setIsMuted(true);
        }
        if (message === "unMuteAudio") {
          setStreamAudioMuted(false);
          setIsMuted(false);
        }

        // Only genuine custom payloads are forwarded to Unreal. Control
        // messages are consumed by the player itself (above, or comms /
        // togglehoveringmouse below) and stream-state is the player's own
        // outbound vocabulary — forwarding any of them hands a strict UE app
        // JSON with no event type ("no event type field" errors, one per
        // embedder heartbeat). passwordSubmit must never reach UE at all.
        const isControlMessage =
          ["heartbeat", "requestScreenshot", "terminateSession",
            "muteAudio", "unMuteAudio", "passwordSubmit"].includes(message) ||
          ["setResolution", "comms", "togglehoveringmouse", "stream-state"]
            .includes(message?.type);

        if (!isControlMessage) handleSendCommand(event.data);
      }

      if (message?.type === "comms") {
        if (message.value === "disconnect") {
          disconnectChatRoom();
        } else {
          connectChatRoom(message.value);
        }
      }

      if (message?.type === "togglehoveringmouse") {
        setIsHovering(message.value);
        PixelStreamingUiApp?.stream._webRtcController.config
          .setFlagEnabled(Flags.HoveringMouseMode, message.value);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [startChatIf, projectId, roomConnect, showChatUI, isHovering, fetchDone]);

  // Clear any pending chat-disconnect teardown timer on unmount.
  useEffect(() => () => {
    if (disconnectTimerRef.current) clearTimeout(disconnectTimerRef.current);
  }, []);

  const handleTerminateSession = () => {
    PixelStreamingApp.disconnect();
    PixelStreamingUiApp?.stream.disconnect();
  };

  const handleSendCommand = (descriptor) => {
    PixelStreamingUiApp?.stream.emitUIInteraction(descriptor);
  };

  const handlePasswordSubmit = async (e, pwd, { silent = false } = {}) => {
    e?.preventDefault();
    const password = pwd || enteredPassword;
    if (!password) return;
    setPasswordError('');
    lastTicketError = null;

    const success = await fetchData({password:password});

    if (success) {
      rememberPassword(projectId, password);
      setPasswordValid(true);
      setLoading(true);
      setShowEnterPassword(false);
      message = { type: "stream-state", value: "hidePassword" };
      window.parent.postMessage(message, "*");
    } else {
      forgetPassword(projectId);
      if (silent) {
        // A remembered password that no longer works (the owner changed it): just ask, no "incorrect" banner
        // for a password the viewer did not type this time.
        setPasswordValid(false);
        setShowEnterPassword(true);
        setLoading(false);
        hideLoadingSpinner();
        return;
      }
      // The gate fails for more than one reason and they need different advice.
      // 429 is the brute-force lockout (10 failures per project+IP in 15 min):
      // telling that viewer "incorrect password" is wrong and makes them retry
      // a password that may well be right.
      const status = lastTicketError?.status;
      if (status === 429) {
        const secs = lastTicketError.retryAfter;
        // Only the hard cap disables the field. Below it the server still checks
        // the password, so someone who knows it must be able to type it — a
        // public IP is shared, and the failures may not even be theirs.
        if (lastTicketError.hard) {
          setLockedUntil(secs > 0 ? Date.now() + secs * 1000 : 0);
          setPasswordError(
            secs > 0
              ? `Too many attempts from this network. Try again in ${formatWait(secs)}.`
              : 'Too many attempts from this network. Please try again later.'
          );
        } else {
          setPasswordError(
            lastTicketError.message || 'Too many incorrect attempts. Check the password and try again.'
          );
        }
      } else if (status === 0) {
        setPasswordError('Could not reach the server. Check your connection and try again.');
      } else if (status && status >= 500) {
        setPasswordError('Something went wrong on our side. Please try again in a moment.');
      } else {
        setPasswordError('Incorrect password. Please try again.');
      }
      setPasswordValid(false);
      setShowEnterPassword(true);
      setLoading(false);
      hideLoadingSpinner();
    }
  };

  // Mute/unmute must touch BOTH elements. Epic's StreamController puts the
  // audio track in its separate <audio> element only when audio and video
  // arrive under different stream ids, which is what a UE streamer sends.
  // The SFU offers both tracks under ONE stream id (mediasoup-sdp-bridge
  // createOffer uses a single sendMsid), so in meetings the audio track
  // stays inside the <video> element, which StartVideoMuted keeps muted, and
  // the <audio> element has no source at all. Toggling only the <audio>
  // element — what this did — flipped an empty element while the sound
  // stayed locked in the muted video: "no audio even after unmute" on every
  // meeting session, while direct sessions worked (2026-09-09).
  // Unmuting the <video> element in direct sessions is harmless: it carries
  // no audio track there.
  function setStreamAudioMuted(muted) {
    if (!PixelStreamingUiApp) return;
    const audioElement = PixelStreamingUiApp.stream._webRtcController?.streamController?.audioElement;
    const videoPlayer = PixelStreamingUiApp.stream.videoElementParent?.querySelector("video");
    if (audioElement) {
      audioElement.muted = muted;
      if (muted) {
        audioElement.pause();
      } else if (audioElement.srcObject) {
        audioElement.play().catch(() => {});
      }
    }
    if (videoPlayer) {
      videoPlayer.muted = muted;
      if (!muted && videoPlayer.paused) {
        videoPlayer.play().catch(() => {});
      }
    }
  }

  const toggleSound = () => {
    const videoPlayer = PixelStreamingUiApp && PixelStreamingUiApp.stream.videoElementParent.querySelector("video");
    if (PixelStreamingUiApp) {
        setStreamAudioMuted(!isMuted);
        setIsMuted(!isMuted);
        if (videoPlayer) {
          videoPlayer.focus();
        }
     }
  };

  const toggleFullscreen = () => {
    const doc = document.documentElement;
    const videoPlayer = PixelStreamingUiApp?.stream.videoElementParent.querySelector("video");
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);

    if (!document.fullscreenElement && !isIOS) {
      (doc.requestFullscreen?.() ||
        doc.mozRequestFullScreen?.() ||
        doc.webkitRequestFullscreen?.() ||
        doc.msRequestFullscreen?.());
      setIsFullscreen(true);
      videoPlayer?.focus();
    } else if (isIOS) {
      if (videoPlayer?.webkitEnterFullscreen) {
        videoPlayer.webkitEnterFullscreen();
        setIsFullscreen(true);
      } else {
        console.warn("Fullscreen not supported on this iOS device.");
      }
    } else {
      (document.exitFullscreen?.() ||
        document.mozCancelFullScreen?.() ||
        document.webkitExitFullscreen?.() ||
        document.msExitFullscreen?.());
      setIsFullscreen(false);
      videoPlayer?.focus();
    }
  };

  const toggleXR = () => PixelStreamingUiApp?.stream.toggleXR();

  const toggleStats = () => {
    if (PixelStreamingUiApp) {
      setStats(getStats());
      setIsModalVisible(prev => !prev);
    }
  };

  const handleResMax = (value, resolution) => {
    const hwsplit = value.split('x');
    activeResolution = resolution;
    if (resolutionMode === "Dynamic Resolution Mode" || resolutionMode === "Crop on Resize Mode") {
      maxHeight = hwsplit[1];
      maxWidth = hwsplit[0];
      handleResize();
    } else if (PixelStreamingUiApp) {
      PixelStreamingUiApp.stream.emitConsoleCommand(`r.SetRes ${value}f`);
      document.querySelector('video')?.focus();
    }
  };

  const handleMessageNumber = (messageLen) => setNewMessage(messageLen);

  const handleRoomConnect = (value) => {
    if (value === true) setSdkConnect(true);
  };

  const handleScreenshot = () => captureScreenshot(PixelStreamingUiApp);

  return (
    <>
      {project && isChatEnabled(project)
        && (sfuPlayerRef.current ? sfuChatActive : (!loading && showUiBtn)) && (
        <VoiceChatUI
          roomConnect={roomConnect}
          roomDisconnect={roomDisconnect}
          toggleMicChat={toggleMicChat}
          onMicStateChange={setToogleMicChat}
          handleMessageNumber={handleMessageNumber}
          handleRoomConnect={handleRoomConnect}
          darkMode={darkMode}
          micStart={toggleMicChat}
          roomName={roomName}
          showChatUiMobile={showChatUiMobile}
          showChatUiDesktop={showChatUiDesktop}
          userName={userName}
          apiBase={API_URL}
          voiceToken={getVoiceToken}
          voiceChat={project.voiceChat}
          textChat={project.textChat}
          videoChat={project.videoChat ?? false}
          position={project.chatPosition}
          streamReady={!loading && showUiBtn}
          avatar={avatar}
          onCloseChatUi={closeChatUiMobile}
          onCloseChatUiDesktop={closeChatUiDesktop}
          onOpenChatUi={openChatUiMobile}
          onOpenChatUiDesktop={openChatUiDesktop}
          onTypingChange={setSomeoneTyping}
        />
      )}
      {isStreamConnect ? (
        <>
          <div className='containMain'>
            <div
              id="videoElement"
              tabIndex={0}
              ref={videoRef}
              style={loading || showEnterPassword || showSsoLogin
                ? { zIndex: -1, position: "relative" }
                : { zIndex: 10005, position: "absolute" }}
            >
                {/* The live view's copy. LoadingOverlay and DisconnectScreen
                    each draw their own, because each is a full-screen surface
                    that has to stand alone — DisconnectScreen replaces this tree
                    entirely, which is how a single shared copy went missing on
                    the terminal-disconnect path. While the overlay is up its
                    opaque backdrop covers this one, so only ever one is seen. */}
                <BrandLogo branding={branding} />

              {!showUiBtn && !(sfuPlayerRef.current && sfuChatActive) && (
                <LoadingOverlay
                  branding={branding}
                  loading={loading}
                  authMessage={authMessage}
                  showEnterPassword={showEnterPassword}
                  enteredPassword={enteredPassword}
                  setEnteredPassword={setEnteredPassword}
                  handlePasswordSubmit={handlePasswordSubmit}
                  passwordError={passwordError}
                  showSsoLogin={showSsoLogin}
                  startSsoLogin={startSsoLogin}
                  status={status}
                  lockedOut={lockedUntil > 0}
                  ssoError={ssoError}
                  unavailable={unavailable}
                />
              )}

              <>
                {status === "active" ? (
                  <>
                    <TcpFallbackToast
                      show={showTcpFallbackToast}
                      onClose={() => setShowTcpFallbackToast(false)}
                    />
                    <MediaHealthToast
                      show={showMediaHealthToast}
                      onRetry={retryOverTcp}
                      onClose={() => setShowMediaHealthToast(false)}
                    />
                    {(sfuPlayerRef.current ? sfuChatActive : showUiBtn) && (
                      <>
                        <ControlPanel
                          project={project}
                          showChatUiMobile={showChatUiMobile}
                          showChatUI={showChatUI}
                          roomDisconnect={roomDisconnect}
                          isMobile={isMobile}
                          showChatUiDesktop={showChatUiDesktop}
                          someoneTyping={someoneTyping}
                          newMessage={newMessage}
                          sdkConnect={sdkConnect}
                          toggleMicChat={toggleMicChat}
                          handleMicChat={handleMicChat}
                          isMuted={isMuted}
                          toggleSound={toggleSound}
                          isFullscreen={isFullscreen}
                          toggleFullscreen={toggleFullscreen}
                          activeResolution={activeResolution}
                          PixelStreamingUiApp={PixelStreamingUiApp}
                          resolutionSetting={resolutionSetting}
                          handleResMax={handleResMax}
                          toggleStats={toggleStats}
                          avgQp={avgQp}
                          primaryCodec={primaryCodec}
                          xrSupport={xrSupport}
                          toggleXR={toggleXR}
                          openChatUiMobile={openChatUiMobile}
                          openChatUiDesktop={openChatUiDesktop}
                        />
                      </>
                    )}
                  </>
                ) : null /* An inactive project is drawn by LoadingOverlay's
                  "unavailable" card. The plain-text "Inactive Project" that used
                  to live here sat UNDERNEATH that overlay's opaque backdrop, so
                  it was never actually visible — and it was a second,
                  differently-styled answer to a question the card now answers. */}
              </>

              {!fetchDone && <LoadingUI message={authMessage} />}

              {isModalVisible && (
                <StatsModal
                  stats={stats}
                  onClose={() => setIsModalVisible(false)}
                />
              )}
            </div>
          </div>
        </>
      ) : (
        <DisconnectScreen disconnectMessage={disconnectMessage} branding={branding} />
      )}
    </>
  );
};

export default App;
